import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
// import { FiCamera, FiVideo } from 'react-icons/fi'; // Video features hidden for now
import styled from 'styled-components';
import {
  TrainingSessionContainer,
  PageSubtitle,
  PageHeader,
  FormSection,
  FormAreaWrapper,
  FormTitle,
  FormGrid,
  FormGroup,
  Label,
  Select,
  Input,
  INPUT_EMPTY_PLACEHOLDER_COLOR,
  TelemarkToggle,
  ToggleButton,
  SubmitButton,
  RecordsSection,
  RecordsTable,
  TableHeader,
  TableCell,
  TableRow,
  MonthDividerRow,
  MonthDividerCell,
  MonthHeader,
  MonthLabel,
  EmptyStateMessage,
} from './styles';
import { TrainingSession, PopulatedAthlete, PopulatedEquipment } from './types';
import { PageTitle } from '../CoachDashboard/styles';
import { athleteAPI, equipmentAPI, trainingSessionAPI } from '../../services/api';
import { hasPlanOrHigher } from '../../utils/subscriptionUtils';
// import VideoRecorder from '../Common/VideoRecorder'; // Video features hidden for now
import MessageModal from '../Common/MessageModal';
import { useAuth } from '../../contexts/AuthContext';

// Type assertion helper for icons to work with React 19
const Icon = ({ component: Component, ...props }: { component: any; [key: string]: any }) => {
  return <Component {...props} />;
};

// MongoDB types
interface Athlete {
  _id: string;
  firstName: string;
  lastName: string;
  subscription?: { plan?: string };
}

interface Equipment {
  _id: string;
  name: string;
  type: 'suit' | 'skis' | 'shoes' | 'bindings';
  athleteId?: string | { _id: string; firstName: string; lastName: string };
}

// New training session form data type
interface NewTrainingSessionFormData {
  trainingType: 'ski_jumping' | 'ground_training' | 'cross_country';
  date: string;
  startTime: string;
  endTime: string;
  location: string;
  weather: string;
  athleteIds: string[];
  sessionId?: string; // Optional session ID for updating existing pre-planned sessions
}

// Jump record type for SKI JUMPING sessions
interface JumpRecord {
  _id: string;
  athleteId: string;
  distance: number;
  startingGate: number;
  hillSize?: string;
  wind?: string;
  speed?: number;
  suitId: string;
  skisId: string;
  shoesId?: string;
  bindingsId?: string;
  telemark: boolean;
  rating?: number;
  videoUrl?: string;
  videoFileName?: string;
  createdAt: string;
}

// Competition jump record type for COMPETITION sessions
interface CompetitionJumpRecord {
  _id: string;
  athleteId: string;
  // First round data
  firstRoundStartingGate?: number;
  firstRoundDistance?: number;
  firstRoundWind?: string;
  firstRoundSpeed?: number;
  firstRoundTelemark?: boolean;
  // Second round data
  secondRoundStartingGate?: number;
  secondRoundDistance?: number;
  secondRoundWind?: string;
  secondRoundSpeed?: number;
  secondRoundTelemark?: boolean;
  // Result
  result?: number;
  // Equipment (same for both rounds)
  suitId: string;
  skisId: string;
  shoesId?: string;
  bindingsId?: string;
  hillSize?: string;
  createdAt: string;
}

// Ground training record type for GROUND TRAINING sessions
interface GroundTrainingRecord {
  _id: string;
  athleteId: string;
  trainingType: 'coordination' | 'power' | 'speed' | 'stretching' | 'specialized_coordination' | 'imitation_ski_jump' | 'run' | 'visualisation';
  intensity: number;
  createdAt: string;
}

interface CrossCountryTrainingRecord {
  _id: string;
  athleteId: string;
  // Placeholder for future cross country training data
  // This will be expanded when cross country data collection is implemented
  createdAt: string;
}

// Training session record type
interface TrainingSessionRecord {
  _id: string;
  trainingType: string;
  date: string;
  startTime: string;
  endTime: string;
  location: string;
  weather: string;
  category?: string;
  athleteIds: (string | PopulatedAthlete)[];
  bananaCupId?: string;
  bananaCupCompetitionId?: string;
  bananaCupCompetitionNumber?: number;
  bananaCupName?: string;
  jumps: JumpRecord[]; // For SKI JUMPING sessions
  competitionJumps: CompetitionJumpRecord[]; // For COMPETITION sessions
  groundTrainings: GroundTrainingRecord[]; // For GROUND TRAINING sessions
  crossCountryTrainings: CrossCountryTrainingRecord[]; // For CROSS COUNTRY sessions
  isPaused?: boolean;
  coachDescription?: string;
  createdAt: string;
}

type SortField = 'date' | 'startTime' | 'endTime' | 'trainingType' | 'location' | 'weather' | 'category' | 'athlete' | 'distance' | 'avgDistance' | 'gate' | 'telemark' | 'avgIntensity';
type SortDirection = 'asc' | 'desc';

const formatNumber = (value: number) => {
  if (!Number.isFinite(value)) {
    return '0';
  }
  return value.toLocaleString('en-US');
};

const normalizeAthleteId = (
  athlete: string | { _id?: string } | PopulatedAthlete | undefined
): string => {
  if (!athlete) return '';
  if (typeof athlete === 'string') return athlete;
  if (typeof athlete === 'object' && '_id' in athlete && athlete._id) {
    return athlete._id;
  }
  const candidate = (athlete as any)?._id;
  return typeof candidate === 'string' ? candidate : '';
};

const getGroundIntensityAverage = (session: TrainingSessionRecord, filterAthleteId?: string, validAthleteIds?: string[]): number | null => {
  if (
    session.trainingType !== 'ground_training' ||
    !session.groundTrainings ||
    session.groundTrainings.length === 0
  ) {
    return null;
  }
  
  // Filter ground training records by athlete ID if filterAthleteId is provided
  const filteredRecords = filterAthleteId && validAthleteIds && validAthleteIds.length > 0
    ? session.groundTrainings.filter(record => {
        const recordAthleteId = (typeof record.athleteId === 'object' && record.athleteId !== null && '_id' in record.athleteId)
          ? (record.athleteId as any)._id.toString()
          : (record.athleteId as string).toString();
        return validAthleteIds.includes(recordAthleteId);
      })
    : filterAthleteId
    ? session.groundTrainings.filter(record => {
        // Fallback to direct comparison if validAthleteIds not provided
        const recordAthleteId = (typeof record.athleteId === 'object' && record.athleteId !== null && '_id' in record.athleteId)
          ? (record.athleteId as any)._id 
          : record.athleteId;
        const normalizedRecordAthleteId = normalizeAthleteId(recordAthleteId as any);
        const normalizedFilterAthleteId = normalizeAthleteId(filterAthleteId as any);
        return normalizedRecordAthleteId === normalizedFilterAthleteId;
      })
    : session.groundTrainings;
  
  if (filteredRecords.length === 0) return null;
  
  const { total, count } = filteredRecords.reduce(
    (acc, record) => ({
      total: acc.total + (record.intensity || 0),
      count: acc.count + 1,
    }),
    { total: 0, count: 0 }
  );
  if (count === 0) return null;
  return total / count;
};

type MonthGroup = {
  key: string;
  label: string;
  sessions: TrainingSessionRecord[];
  sortValue: number;
};

const groupSessionsByMonth = (sessions: TrainingSessionRecord[]): MonthGroup[] => {
  const monthMap = new Map<string, MonthGroup>();
  sessions.forEach(session => {
    if (!session.date) return;
    const date = new Date(session.date);
    if (Number.isNaN(date.getTime())) return;
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    if (!monthMap.has(key)) {
      monthMap.set(key, {
        key,
        label: date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
        sessions: [],
        sortValue: new Date(date.getFullYear(), date.getMonth(), 1).getTime(),
      });
    }
    monthMap.get(key)!.sessions.push(session);
  });
  return Array.from(monthMap.values())
    .sort((a, b) => b.sortValue - a.sortValue);
};

const responsiveJumpGrid: React.CSSProperties = {
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: '0.7rem 0.8rem'
};

// Custom grid styles for jump form rows
const jumpFormRow1: React.CSSProperties = {
  gridTemplateColumns: '1fr 1fr',
  gap: '0.5rem 0.8rem',
  alignItems: 'start'
};

const jumpFormRow2: React.CSSProperties = {
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: '0.7rem 0.8rem'
};

const jumpFormRow3: React.CSSProperties = {
  gridTemplateColumns: '80px 100px 100px 100px',
  gap: '0.5rem 0.6rem',
  alignItems: 'start'
};

const jumpFormRow4: React.CSSProperties = {
  gridTemplateColumns: '1fr 1fr',
  gap: '0.7rem 0.8rem'
};

interface TrainingSessionsProps {
  athleteId?: string; // Optional prop to filter by athlete and lock selection
  validAthleteIds?: string[]; // List of all valid athlete IDs (own ID + coach athlete record IDs) for filtering records
}

// Styled components for confirmation modal - matches app modal design (AthleteForm, DashboardSettings, etc.)
const ConfirmationModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  padding: 1.5rem;
  animation: fadeIn 0.3s ease;

  @media (max-width: 768px) {
    padding: 1rem;
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ConfirmationModalContent = styled.div`
  background: var(--bg-sidebar);
  padding: 2rem 2rem 1.75rem;
  border-radius: 1rem;
  width: 90%;
  max-width: 440px;
  border: none;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  animation: modalSlideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 1.5rem 1.25rem 1.5rem;
    max-height: 90vh;
    overflow-y: auto;
  }

  @keyframes modalSlideIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }

  h2 {
    font-size: 1.25rem;
    margin: 0 0 1.25rem 0;
    font-family: 'Poppins', sans-serif;
    color: var(--accent-orange);
    font-weight: 900;
    text-align: left;
    letter-spacing: -0.02em;
  }
`;

const ConfirmationButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.25rem;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 0.75rem;
  }
`;

/** On mobile: order = Athlete+Hill (1), Gate/Distance/Wind/Speed (2), Rating (3), Equipment (4), Telemark (5). Telemark section centered. */
const JumpFormFieldsWrapper = styled.div`
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    .jump-form-row-1 { order: 1; }
    .jump-form-row-2 { order: 4; }
    .jump-form-row-3 { order: 2; }
    .jump-form-row-4 {
      display: contents;
    }
    .jump-form-row-4-rating { order: 3; }
    .jump-form-row-4-telemark {
      order: 5;
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
    }
  }
`;

const ConfirmationModalButton = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 0.6rem 1.25rem;
  border-radius: 0.5rem;
  border: 2px solid var(--accent-orange);
  background: ${props => props.$variant === 'primary' ? 'var(--accent-orange)' : 'transparent'};
  color: ${props => props.$variant === 'primary' ? '#fff' : 'var(--accent-orange)'};
  font-family: 'Poppins', sans-serif;
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 120px;
  min-height: 2.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  box-shadow: ${props => props.$variant === 'primary' ? '0 2px 8px rgba(255, 150, 0, 0.3)' : 'none'};

  @media (max-width: 768px) {
    flex: 1;
    min-width: 0;
  }

  &:hover {
    background: var(--accent-orange-dark);
    color: #fff;
    border-color: var(--accent-orange-dark);
    box-shadow: ${props => props.$variant === 'primary' ? '0 4px 12px rgba(255, 150, 0, 0.35)' : 'none'};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ConfirmationModalBody = styled.div`
  color: var(--text-gray);
  font-size: 0.95rem;
  line-height: 1.5;
  margin-bottom: 1.25rem;

  p {
    margin: 0 0 0.5rem 0;
    font-family: 'Poppins', sans-serif;
  }

  p:last-child {
    margin-bottom: 0;
    font-size: 0.85rem;
    opacity: 0.9;
  }
`;

const TrainingSessions: React.FC<TrainingSessionsProps> = ({ athleteId: filterAthleteId, validAthleteIds = [] }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [trainingSessions, setTrainingSessions] = useState<TrainingSessionRecord[]>([]);
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(new Set());
  const [expandedMonthsSkiJumping, setExpandedMonthsSkiJumping] = useState<Set<string>>(new Set());
  const [expandedMonthsGroundTraining, setExpandedMonthsGroundTraining] = useState<Set<string>>(new Set());
  const [expandedMonthsCrossCountry, setExpandedMonthsCrossCountry] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [jumpSortField, setJumpSortField] = useState<string>('');
  const [jumpSortDirection, setJumpSortDirection] = useState<'asc' | 'desc'>('asc');
  const [groundTrainingSortField, setGroundTrainingSortField] = useState<string>('');
  const [groundTrainingSortDirection, setGroundTrainingSortDirection] = useState<'asc' | 'desc'>('asc');
  const [crossCountrySortField, setCrossCountrySortField] = useState<string>('');
  const [crossCountrySortDirection, setCrossCountrySortDirection] = useState<'asc' | 'desc'>('asc');
  const [formData, setFormData] = useState<Omit<TrainingSession, '_id' | 'createdAt' | 'updatedAt'> & { 
    hillSize: string; 
    wind: string;
    speed: number;
    rating: number;
    // Competition-specific fields
    firstRoundStartingGate: number;
    firstRoundDistance: number;
    firstRoundWind: string;
    firstRoundSpeed: number;
    firstRoundTelemark: boolean;
    secondRoundStartingGate: number;
    secondRoundDistance: number;
    secondRoundWind: string;
    secondRoundSpeed: number;
    secondRoundTelemark: boolean;
    result: number;
    // Ground training fields
    groundTrainingType: 'coordination' | 'power' | 'speed' | 'stretching' | 'specialized_coordination' | 'imitation_ski_jump' | 'run' | 'visualisation';
    intensity: number;
  }>({
    athleteId: '',
    date: new Date().toISOString().split('T')[0], // Keep this for database compatibility
    distance: 0,
    startingGate: 0,
    suitId: '',
    skisId: '',
    shoesId: '',
    bindingsId: '',
    telemark: false,
    hillSize: '',
    wind: '',
    speed: 0,
    rating: 0,
    // Competition-specific fields
    firstRoundStartingGate: 0,
    firstRoundDistance: 0,
    firstRoundWind: '',
    firstRoundSpeed: 0,
    firstRoundTelemark: false,
    secondRoundStartingGate: 0,
    secondRoundDistance: 0,
    secondRoundWind: '',
    secondRoundSpeed: 0,
    secondRoundTelemark: false,
    result: 0,
    // Ground training fields
    groundTrainingType: 'coordination',
    intensity: 0,
  });
  
  // New training session form state
  const [newTrainingSessionData, setNewTrainingSessionData] = useState<NewTrainingSessionFormData>({
    trainingType: 'ski_jumping',
    date: new Date().toISOString().split('T')[0],
    startTime: '',
    endTime: '',
    location: '',
    weather: '',
    athleteIds: [],
  });
  
  // State to control visibility of the jump form
  const [showJumpForm, setShowJumpForm] = useState(false);
  const [showVideoRecorder, setShowVideoRecorder] = useState(false);
  const [showGroundTrainingForm, setShowGroundTrainingForm] = useState(false);
  // State to control if training session is active
  const [isTrainingSessionActive, setIsTrainingSessionActive] = useState(false);
  // State to control visibility of the new training session form
  const [showNewTrainingSessionForm, setShowNewTrainingSessionForm] = useState(false);
  
  // Edit state
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editingJumpId, setEditingJumpId] = useState<string | null>(null);
  const [editingGroundTrainingId, setEditingGroundTrainingId] = useState<string | null>(null);
  const [editingCrossCountryId, setEditingCrossCountryId] = useState<string | null>(null);
  const [editingSessionData, setEditingSessionData] = useState<NewTrainingSessionFormData | null>(null);
  
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [autoPopulatedFields, setAutoPopulatedFields] = useState<Set<string>>(new Set());
  const [jumps, setJumps] = useState<JumpRecord[]>([]);
  const [groundTrainings, setGroundTrainings] = useState<GroundTrainingRecord[]>([]);
  const [coachDescription, setCoachDescription] = useState<string>('');
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [rawInputValues, setRawInputValues] = useState<{ distance: string; speed: string; rating: string }>({
    distance: '',
    speed: '',
    rating: ''
  });
  const [showFinishConfirmation, setShowFinishConfirmation] = useState(false);
  const [equipmentMessageField, setEquipmentMessageField] = useState<'suit' | 'skis' | 'shoes' | 'bindings' | null>(null);
  const [equipmentMessageDismissing, setEquipmentMessageDismissing] = useState(false);
  const equipmentMessagePopoverRef = useRef<HTMLDivElement>(null);
  const equipmentRowRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef<number>(0);
  const editFormRef = useRef<HTMLDivElement>(null);
  const jumpFormRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!equipmentMessageField) return;
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      const popover = equipmentMessagePopoverRef.current;
      const row = equipmentRowRef.current;
      if (popover?.contains(target) || row?.contains(target)) return;
      setEquipmentMessageDismissing(true);
      setTimeout(() => {
        setEquipmentMessageField(null);
        setEquipmentMessageDismissing(false);
      }, 250);
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [equipmentMessageField]);

  useEffect(() => {
    // Fetch athletes, equipment, and all training sessions (no pagination)
    const fetchData = async () => {
      try {
        setIsLoading(true);
        let sessionsResponse;
        if (filterAthleteId) {
          sessionsResponse = await trainingSessionAPI.getByAthlete(filterAthleteId, undefined, undefined, true);
        } else {
          sessionsResponse = await trainingSessionAPI.getAll(undefined, undefined, true);
        }
        const sessionsArray = Array.isArray(sessionsResponse) ? sessionsResponse : (sessionsResponse?.sessions || []);
        setTrainingSessions(sessionsArray);

        const [athleteData, equipmentData] = await Promise.all([
          athleteAPI.getAll(),
          equipmentAPI.getAll(),
        ]);

        setAthletes(athleteData);
        setEquipment(equipmentData);

        // If filterAthleteId is provided, auto-select that athlete in the form
        if (filterAthleteId) {
          setNewTrainingSessionData(prev => ({
            ...prev,
            athleteIds: [filterAthleteId],
          }));
        }
        
        setError(null);
      } catch (err) {
        setError('Failed to load data. Please try again.');
        console.error('Error fetching data:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [filterAthleteId]);

  const hasRestoredSessionRef = useRef(false);
  // Restore active session when returning to the page (e.g. after navigating away)
  useEffect(() => {
    if (trainingSessions.length === 0) return;
    if (hasRestoredSessionRef.current) return;
    const storedSessionId = (window as any).currentSessionId;
    if (!storedSessionId) return;
    const session = trainingSessions.find(s => s._id === storedSessionId);
    if (!session) return;
    hasRestoredSessionRef.current = true;
    setIsTrainingSessionActive(true);
    if (session.trainingType === 'ski_jumping') {
      setShowJumpForm(true);
      setExpandedSessions(prev => new Set(prev).add(session._id));
    } else if (session.trainingType === 'ground_training') {
      setShowGroundTrainingForm(true);
      setExpandedSessions(prev => new Set(prev).add(session._id));
    } else {
      setExpandedSessions(prev => new Set(prev).add(session._id));
    }
  }, [trainingSessions]);

  // Handle pre-filled session data from Training Planner or open form from Coach Home
  useEffect(() => {
    const state = location.state as { prefillSession?: NewTrainingSessionFormData; openNewSessionForm?: boolean } | null;
    if (state?.openNewSessionForm) {
      setShowNewTrainingSessionForm(true);
      window.history.replaceState({}, document.title);
      return;
    }
    if (state?.prefillSession) {
      const prefillData = state.prefillSession;
      setNewTrainingSessionData({
        trainingType: prefillData.trainingType,
        date: prefillData.date,
        startTime: prefillData.startTime,
        endTime: prefillData.endTime,
        location: prefillData.location,
        weather: prefillData.weather || '',
        athleteIds: prefillData.athleteIds || [],
        sessionId: prefillData.sessionId, // Store session ID if updating existing session
      });
      setShowNewTrainingSessionForm(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Initialize ground training records when ground training form is shown
  useEffect(() => {
    if (showGroundTrainingForm && newTrainingSessionData.athleteIds.length > 0) {
      const initialGroundTrainings = newTrainingSessionData.athleteIds.map(athleteId => ({
        _id: '',
        athleteId,
        trainingType: 'coordination' as const,
        intensity: 1,
        createdAt: '',
      }));
      setGroundTrainings(initialGroundTrainings);
    }
  }, [showGroundTrainingForm, newTrainingSessionData.athleteIds]);



  // Effect to auto-populate form when athlete is selected and training sessions data is available
  useEffect(() => {
    if (formData.athleteId && trainingSessions.length > 0) {
      // Re-run auto-population when training sessions data changes
      const lastValues = getLastUsedValuesForAthlete(formData.athleteId);
      if (lastValues) {
        const athleteSuits = getEquipmentByType('suit', formData.athleteId);
        const athleteSkis = getEquipmentByType('skis', formData.athleteId);
        const athleteShoes = getEquipmentByType('shoes', formData.athleteId);
        const athleteBindings = getEquipmentByType('bindings', formData.athleteId);
        
        setFormData(prev => ({
          ...prev,
          suitId: athleteSuits.some(suit => suit._id === lastValues.suitId) ? lastValues.suitId : prev.suitId,
          skisId: athleteSkis.some(skis => skis._id === lastValues.skisId) ? lastValues.skisId : prev.skisId,
          shoesId: athleteShoes.some(shoes => shoes._id === lastValues.shoesId) ? lastValues.shoesId : prev.shoesId,
          bindingsId: athleteBindings.some(bindings => bindings._id === lastValues.bindingsId) ? lastValues.bindingsId : prev.bindingsId,
          hillSize: lastValues.hillSize || prev.hillSize,
          startingGate: lastValues.startingGate || prev.startingGate,
          wind: lastValues.wind || prev.wind,
        }));
      }
    }
  }, [trainingSessions, formData.athleteId]);

  // Helper function to normalize decimal separators (comma to period)
  const normalizeDecimalInput = (value: string): string => {
    // Replace comma with period for decimal separator
    return value.replace(',', '.');
  };

  // Helper function to parse numeric value, handling both comma and period as decimal separator
  const parseNumericValue = (value: string): number => {
    if (!value || value.trim() === '') return 0;
    // Normalize comma to period, then parse
    const normalized = normalizeDecimalInput(value.trim());
    const parsed = Number(normalized);
    return isNaN(parsed) ? 0 : parsed;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Clear auto-populated indicator for this field when user manually changes it
    if (autoPopulatedFields.has(name)) {
      setAutoPopulatedFields(prev => {
        const newSet = new Set(prev);
        newSet.delete(name);
        return newSet;
      });
    }

    // Restrict to numbers only for jump numeric fields
    let nextValue: string | number = value;
    if (name === 'hillSize') {
      nextValue = value.replace(/\D/g, '');
    } else if (name === 'wind') {
      // Allow digits, minus, comma and period (decimal separators); normalize comma to dot
      const s = value.replace(/,/g, '.').replace(/[^\d.-]/g, '');
      const minus = s.startsWith('-') ? '-' : '';
      const rest = s.replace(/-/g, '');
      const parts = rest.split('.');
      nextValue = minus + (parts[0] || '') + (parts.length > 1 ? '.' + parts.slice(1).join('').slice(0, 2) : '');
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: name === 'distance' || name === 'startingGate' || name === 'speed' 
        ? parseNumericValue(value)
        : (name === 'hillSize' || name === 'wind' ? nextValue : value),
    }));
  };

  // Function to get the last used values for an athlete
  const getLastUsedValuesForAthlete = (athleteId: string) => {
    // Find all jumps for this athlete across all training sessions (both regular and competition)
    const allJumpsWithSessionInfo = trainingSessions.flatMap(session => {
      const regularJumps = session.jumps.map(jump => ({
        ...jump,
        sessionDate: session.date,
        sessionCreatedAt: session.createdAt,
        isCompetition: false
      }));
      
      const competitionJumps = (session.competitionJumps || []).map(jump => ({
        ...jump,
        sessionDate: session.date,
        sessionCreatedAt: session.createdAt,
        isCompetition: true
      }));
      
      return [...regularJumps, ...competitionJumps];
    });
    
    const athleteJumps = allJumpsWithSessionInfo.filter(jump => jump.athleteId === athleteId);
    
    if (athleteJumps.length === 0) {
      return null;
    }
    
    // Sort by session date and creation time to get the most recent
    const sortedJumps = athleteJumps.sort((a, b) => {
      const dateComparison = new Date(b.sessionDate).getTime() - new Date(a.sessionDate).getTime();
      if (dateComparison !== 0) return dateComparison;
      return new Date(b.sessionCreatedAt).getTime() - new Date(a.sessionCreatedAt).getTime();
    });
    
    const lastJump = sortedJumps[0];
    
    if (lastJump.isCompetition) {
      // Return competition jump data
      const competitionJump = lastJump as CompetitionJumpRecord & { sessionDate: string; sessionCreatedAt: string; isCompetition: boolean };
      return {
        suitId: competitionJump.suitId,
        skisId: competitionJump.skisId,
        shoesId: competitionJump.shoesId || '',
        bindingsId: competitionJump.bindingsId || '',
        hillSize: competitionJump.hillSize || '',
        startingGate: competitionJump.firstRoundStartingGate || 0,
        wind: competitionJump.firstRoundWind || '',
        speed: competitionJump.firstRoundSpeed || 0,
        // For competition, use the actual competition values
        firstRoundStartingGate: competitionJump.firstRoundStartingGate || 0,
        firstRoundDistance: competitionJump.firstRoundDistance || 0,
        firstRoundWind: competitionJump.firstRoundWind || '',
        firstRoundSpeed: competitionJump.firstRoundSpeed || 0,
        firstRoundTelemark: competitionJump.firstRoundTelemark || false,
        secondRoundStartingGate: competitionJump.secondRoundStartingGate || 0,
        secondRoundDistance: competitionJump.secondRoundDistance || 0,
        secondRoundWind: competitionJump.secondRoundWind || '',
        secondRoundSpeed: competitionJump.secondRoundSpeed || 0,
        secondRoundTelemark: competitionJump.secondRoundTelemark || false,
      };
    } else {
      // Return regular jump data
      const regularJump = lastJump as JumpRecord & { sessionDate: string; sessionCreatedAt: string; isCompetition: boolean };
      return {
        suitId: regularJump.suitId,
        skisId: regularJump.skisId,
        shoesId: regularJump.shoesId || '',
        bindingsId: regularJump.bindingsId || '',
        hillSize: regularJump.hillSize || '',
        startingGate: regularJump.startingGate,
        wind: regularJump.wind || '',
        speed: regularJump.speed || 0,
        // For competition, use the same values for both rounds initially
        firstRoundStartingGate: regularJump.startingGate,
        firstRoundDistance: regularJump.distance,
        firstRoundWind: regularJump.wind || '',
        firstRoundSpeed: regularJump.speed || 0,
        firstRoundTelemark: regularJump.telemark,
        secondRoundStartingGate: regularJump.startingGate,
        secondRoundDistance: regularJump.distance,
        secondRoundWind: regularJump.wind || '',
        secondRoundSpeed: regularJump.speed || 0,
        secondRoundTelemark: regularJump.telemark,
      };
    }
  };

  // Function to handle athlete selection in jump form
  const handleAthleteSelectionInJumpForm = (athleteId: string) => {
    // Get last used values for this athlete
    const lastValues = getLastUsedValuesForAthlete(athleteId);
    
    // Check if the last used equipment belongs to this athlete
    const athleteSuits = getEquipmentByType('suit', athleteId);
    const athleteSkis = getEquipmentByType('skis', athleteId);
    const athleteShoes = getEquipmentByType('shoes', athleteId);
    const athleteBindings = getEquipmentByType('bindings', athleteId);
    
    // Track which fields will be auto-populated
    const populatedFields = new Set<string>();
    
    if (lastValues) {
      if (athleteSuits.some(suit => suit._id === lastValues.suitId)) populatedFields.add('suitId');
      if (athleteSkis.some(skis => skis._id === lastValues.skisId)) populatedFields.add('skisId');
      if (athleteShoes.some(shoes => shoes._id === lastValues.shoesId)) populatedFields.add('shoesId');
      if (athleteBindings.some(bindings => bindings._id === lastValues.bindingsId)) populatedFields.add('bindingsId');
      if (lastValues.hillSize) populatedFields.add('hillSize');
      if (lastValues.startingGate) populatedFields.add('startingGate');
      if (lastValues.wind) populatedFields.add('wind');
      if (lastValues.speed) populatedFields.add('speed');
      // Add competition fields
      if (lastValues.firstRoundStartingGate) populatedFields.add('firstRoundStartingGate');
      if (lastValues.firstRoundDistance) populatedFields.add('firstRoundDistance');
      if (lastValues.firstRoundWind) populatedFields.add('firstRoundWind');
      if (lastValues.firstRoundSpeed) populatedFields.add('firstRoundSpeed');
      if (lastValues.secondRoundStartingGate) populatedFields.add('secondRoundStartingGate');
      if (lastValues.secondRoundDistance) populatedFields.add('secondRoundDistance');
      if (lastValues.secondRoundWind) populatedFields.add('secondRoundWind');
      if (lastValues.secondRoundSpeed) populatedFields.add('secondRoundSpeed');
    }
    
    setAutoPopulatedFields(populatedFields);
    
    // Update all form data in a single state update
    setFormData(prev => ({
      ...prev,
      athleteId,
      // Auto-populate equipment if the athlete still owns it and we have last values
      suitId: lastValues && athleteSuits.some(suit => suit._id === lastValues.suitId) ? lastValues.suitId : '',
      skisId: lastValues && athleteSkis.some(skis => skis._id === lastValues.skisId) ? lastValues.skisId : '',
      shoesId: lastValues && athleteShoes.some(shoes => shoes._id === lastValues.shoesId) ? lastValues.shoesId : '',
      bindingsId: lastValues && athleteBindings.some(bindings => bindings._id === lastValues.bindingsId) ? lastValues.bindingsId : '',
      // Auto-populate jump data (always populate these if we have last values)
      hillSize: lastValues ? lastValues.hillSize : '',
      startingGate: lastValues ? lastValues.startingGate : 0,
      wind: lastValues ? lastValues.wind : '',
      speed: lastValues ? lastValues.speed : 0,
      // Auto-populate competition fields
      firstRoundStartingGate: lastValues ? lastValues.firstRoundStartingGate : 0,
      firstRoundDistance: lastValues ? lastValues.firstRoundDistance : 0,
      firstRoundWind: lastValues ? lastValues.firstRoundWind : '',
      firstRoundSpeed: lastValues ? lastValues.firstRoundSpeed : 0,
      firstRoundTelemark: lastValues ? lastValues.firstRoundTelemark : false,
      secondRoundStartingGate: lastValues ? lastValues.secondRoundStartingGate : 0,
      secondRoundDistance: lastValues ? lastValues.secondRoundDistance : 0,
      secondRoundWind: lastValues ? lastValues.secondRoundWind : '',
      secondRoundSpeed: lastValues ? lastValues.secondRoundSpeed : 0,
      secondRoundTelemark: lastValues ? lastValues.secondRoundTelemark : false,
    }));
    
    // Update raw input values for decimal fields
    if (lastValues) {
      // Get the actual last jump to access distance and rating
      const allJumpsWithSessionInfo = trainingSessions.flatMap(session => {
        const regularJumps = session.jumps.map(jump => ({
          ...jump,
          sessionDate: session.date,
          sessionCreatedAt: session.createdAt,
          isCompetition: false
        }));
        const competitionJumps = (session.competitionJumps || []).map(jump => ({
          ...jump,
          sessionDate: session.date,
          sessionCreatedAt: session.createdAt,
          isCompetition: true
        }));
        return [...regularJumps, ...competitionJumps];
      });
      const athleteJumps = allJumpsWithSessionInfo.filter(jump => jump.athleteId === athleteId);
      if (athleteJumps.length > 0) {
        const sortedJumps = athleteJumps.sort((a, b) => {
          const dateComparison = new Date(b.sessionDate).getTime() - new Date(a.sessionDate).getTime();
          if (dateComparison !== 0) return dateComparison;
          return new Date(b.sessionCreatedAt).getTime() - new Date(a.sessionCreatedAt).getTime();
        });
        const lastJump = sortedJumps[0];
        const distance = lastJump.isCompetition 
          ? (lastJump as any).firstRoundDistance || 0
          : (lastJump as any).distance || 0;
        const rating = (lastJump as any).rating || 0;
        
        setRawInputValues({
          distance: distance ? String(distance).replace('.', ',') : '0',
          speed: lastValues.speed ? String(lastValues.speed).replace('.', ',') : '0',
          rating: rating ? String(rating).replace('.', ',') : '0'
        });
      } else {
        setRawInputValues({ distance: '', speed: '', rating: '' });
      }
    } else {
      setRawInputValues({ distance: '', speed: '', rating: '' });
    }
    
    // Clear auto-populated indicator after 3 seconds
    setTimeout(() => {
      setAutoPopulatedFields(new Set());
    }, 3000);
  };

  const handleTelemarkToggle = (value: boolean) => {
    setFormData(prev => ({ ...prev, telemark: value }));
  };

  const handleFirstRoundTelemarkToggle = (value: boolean) => {
    setFormData(prev => ({ ...prev, firstRoundTelemark: value }));
  };

  const handleSecondRoundTelemarkToggle = (value: boolean) => {
    setFormData(prev => ({ ...prev, secondRoundTelemark: value }));
  };

  // New training session form handlers
  const handleNewTrainingSessionChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Update the form data first
    setNewTrainingSessionData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    // Validate that end time is after start time
    if (name === 'startTime' || name === 'endTime') {
      const updatedData = { ...newTrainingSessionData, [name]: value };
      
      if (updatedData.startTime && updatedData.endTime) {
        const startTime = new Date(`2000-01-01T${updatedData.startTime}`);
        const endTime = new Date(`2000-01-01T${updatedData.endTime}`);
        
        if (endTime <= startTime) {
          setError('invalid_end_time');
        } else {
          setError(null);
        }
      } else {
        // Clear error if one of the times is empty
        setError(null);
      }
    }
  };

  const handleAthleteSelection = (athleteId: string) => {
    // If filterAthleteId is provided, don't allow changing athlete selection
    if (filterAthleteId) {
      return;
    }
    
    setNewTrainingSessionData(prev => ({
      ...prev,
      athleteIds: prev.athleteIds.includes(athleteId)
        ? prev.athleteIds.filter(id => id !== athleteId)
        : [...prev.athleteIds, athleteId],
    }));
  };

  const handleNewTrainingSessionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // If editing, use update handler
    if (editingSessionId) {
      return handleUpdateSession(e);
    }
    
    // Validate that end time is after start time before submitting
    if (newTrainingSessionData.startTime && newTrainingSessionData.endTime) {
      const startTime = new Date(`2000-01-01T${newTrainingSessionData.startTime}`);
      const endTime = new Date(`2000-01-01T${newTrainingSessionData.endTime}`);
      
      if (endTime <= startTime) {
        setError('invalid_end_time');
        return;
      }
    }
    
    // Validate that at least one athlete is selected
    if (newTrainingSessionData.athleteIds.length === 0) {
      setError('no_athletes_selected');
      return;
    }
    
    try {
      console.log('Creating training session with data:', newTrainingSessionData);
      
      let sessionId: string;
      
      // If sessionId exists, update the existing pre-planned session instead of creating new
      if (newTrainingSessionData.sessionId) {
        // Update existing session
        const updateData: any = {
          trainingType: newTrainingSessionData.trainingType,
          date: newTrainingSessionData.date,
          startTime: newTrainingSessionData.startTime,
          endTime: newTrainingSessionData.endTime,
          location: newTrainingSessionData.location,
          athleteIds: newTrainingSessionData.athleteIds,
          weather: newTrainingSessionData.weather || undefined,
        };
        
        const updatedSession = await trainingSessionAPI.update(newTrainingSessionData.sessionId, updateData);
        sessionId = updatedSession._id;
        
        // Update in local state
        setTrainingSessions(prev => prev.map(s => s._id === updatedSession._id ? updatedSession : s));
      } else {
        // Create new training session
        const baseSessionData: any = {
          trainingType: newTrainingSessionData.trainingType,
          date: newTrainingSessionData.date,
          startTime: newTrainingSessionData.startTime,
          endTime: newTrainingSessionData.endTime,
          location: newTrainingSessionData.location,
          athleteIds: newTrainingSessionData.athleteIds,
          jumps: []
        };

        // Add weather field for all training types
        let sessionData = { 
          ...baseSessionData,
          weather: newTrainingSessionData.weather || undefined,
        };

        const newTrainingSession = await trainingSessionAPI.create(sessionData);
        sessionId = newTrainingSession._id;
        
        // Add to training sessions
        setTrainingSessions(prev => [newTrainingSession, ...prev]);
      }
      
      console.log('Training session saved successfully, ID:', sessionId);
      
      // Show jump form for SKI JUMPING and QUALIFICATIONS training types
      if (newTrainingSessionData.trainingType === 'ski_jumping') {
        setShowJumpForm(true);
        setIsTrainingSessionActive(true);
        // Auto-fill athlete if filterAthleteId is provided
        if (filterAthleteId && newTrainingSessionData.athleteIds.includes(filterAthleteId)) {
          handleAthleteSelectionInJumpForm(filterAthleteId);
        }
        setFormData(prev => ({ ...prev, date: newTrainingSessionData.date }));
        (window as any).currentSessionId = sessionId;
        setTimeout(() => jumpFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
      } else if (newTrainingSessionData.trainingType === 'ground_training') {
        setShowGroundTrainingForm(true);
        setIsTrainingSessionActive(true);
        (window as any).currentSessionId = sessionId;
      } else {
        // For CROSS COUNTRY, reset the form immediately
        setNewTrainingSessionData({
          trainingType: 'ski_jumping',
          date: new Date().toISOString().split('T')[0],
          startTime: '',
          endTime: '',
          location: '',
          weather: '',
          athleteIds: [],
        });
      }
      
      setError(null);
    } catch (err) {
      console.error('Detailed error creating training session:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(`Failed to create training session: ${errorMessage}`);
    }
  };

  const handleFinishTrainingSession = () => {
    // Show confirmation modal first
    setShowFinishConfirmation(true);
  };

  const confirmFinishTrainingSession = async () => {
    try {
      console.log('Finishing Training Session');
      
      const currentSessionId = (window as any).currentSessionId;
      if (currentSessionId && coachDescription.trim()) {
        // Save coach description to the session
        await trainingSessionAPI.update(currentSessionId, {
          coachDescription: coachDescription.trim()
        });
        
        // Refresh training sessions - reload all
        let updatedSessionsResponse;
        if (filterAthleteId) {
          updatedSessionsResponse = await trainingSessionAPI.getByAthlete(filterAthleteId, undefined, undefined, true);
        } else {
          updatedSessionsResponse = await trainingSessionAPI.getAll(undefined, undefined, true);
        }
        const sessionsArray = Array.isArray(updatedSessionsResponse) ? updatedSessionsResponse : (updatedSessionsResponse?.sessions || []);
        setTrainingSessions(sessionsArray);
      }
      
      // Reset all states
      setIsTrainingSessionActive(false);
      setShowJumpForm(false);
      setShowGroundTrainingForm(false);
      setShowNewTrainingSessionForm(false); // Close the new training session form
      setShowFinishConfirmation(false); // Close confirmation modal
      setCoachDescription('');
      setNewTrainingSessionData({
        trainingType: 'ski_jumping',
        date: new Date().toISOString().split('T')[0],
        startTime: '',
        endTime: '',
        location: '',
        weather: '',
        athleteIds: [],
      });
      setFormData(prev => ({
        ...prev,
        distance: 0,
        startingGate: 0,
        telemark: false,
        hillSize: '',
        wind: '',
        speed: 0,
        rating: 0,
        // Reset competition fields
        firstRoundStartingGate: 0,
        firstRoundDistance: 0,
        firstRoundWind: '',
        firstRoundSpeed: 0,
        firstRoundTelemark: false,
        secondRoundStartingGate: 0,
        secondRoundDistance: 0,
        secondRoundWind: '',
        secondRoundSpeed: 0,
        secondRoundTelemark: false,
        result: 0,
      }));
      setRawInputValues({ distance: '', speed: '', rating: '' });
      
      // Clear stored session ID
      (window as any).currentSessionId = null;
      
      setError(null);
    } catch (err) {
      setError('Failed to finish training session. Please try again.');
      console.error('Error finishing training session:', err);
      setShowFinishConfirmation(false);
    }
  };

  // Note: handleDeleteTrainingSession is currently unused but may be needed for future functionality

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // If editing, use update handler
    if (editingJumpId) {
      return handleUpdateJump(e);
    }
    
    try {
      // Get the current session ID
      const currentSessionId = (window as any).currentSessionId;
      
      if (currentSessionId) {
        // For regular training, use the standard jump format
        const jumpData = {
          athleteId: formData.athleteId,
          distance: formData.distance,
          startingGate: formData.startingGate,
          suitId: formData.suitId || undefined,
          skisId: formData.skisId || undefined,
          shoesId: formData.shoesId || undefined,
          bindingsId: formData.bindingsId || undefined,
          telemark: formData.telemark,
          hillSize: formData.hillSize,
          wind: formData.wind,
          speed: formData.speed,
          rating: formData.rating || undefined,
        };
        
        // Add jump to the training session in MongoDB
        const updatedSession = await trainingSessionAPI.addJump(currentSessionId, jumpData);
        
        // Update the training session in local state
        if (updatedSession) {
          setTrainingSessions(prev => prev.map(session => 
            session._id === currentSessionId ? updatedSession : session
          ));
        }
        
        // Show success notification
        setNotification({ message: 'Jump successfully added!', type: 'success' });
        setTimeout(() => setNotification(null), 3000);
        
        // On mobile, scroll to top of add-jump section so user can add another
        if (window.innerWidth <= 768) {
          setTimeout(() => {
            jumpFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 150);
        }
        
        // Reset jump form
        setFormData(prev => ({
          ...prev,
          distance: 0,
          startingGate: 0,
          telemark: false,
          hillSize: '',
          wind: '',
          speed: 0,
          rating: 0,
        }));
        setRawInputValues({ distance: '', speed: '', rating: '' });
        
        setError(null);
      }
    } catch (err) {
      setError('Failed to save training session. Please try again.');
      console.error('Error saving training session:', err);
    }
  };

  const getAthleteName = (athleteId: string | PopulatedAthlete) => {
    // Handle both string ID and populated athlete object
    if (typeof athleteId === 'object' && athleteId !== null) {
      return `${athleteId.firstName} ${athleteId.lastName}`;
    }
    const athlete = athletes.find(a => a._id === athleteId);
    return athlete ? `${athlete.firstName} ${athlete.lastName}` : 'Unknown';
  };

  const getEquipmentName = (equipmentId: string | PopulatedEquipment) => {
    // Handle both string ID and populated equipment object
    if (typeof equipmentId === 'object' && equipmentId !== null) {
      return equipmentId.name;
    }
    const eq = equipment.find(e => e._id === equipmentId);
    return eq ? eq.name : 'Unknown';
  };

  const getEquipmentByType = (type: 'suit' | 'skis' | 'shoes' | 'bindings', athleteId?: string) => {
    if (athleteId) {
      // Find the selected athlete record
      const selectedAthlete = athletes.find(a => a._id === athleteId);
      
      // Build list of athlete IDs to search for equipment
      const athleteIdsToSearch: string[] = [athleteId];
      
      // If this is a coach athlete record that references an original athlete (connected via ID)
      if (selectedAthlete && (selectedAthlete as any).athleteId) {
        // Add the original athlete's ID
        athleteIdsToSearch.push((selectedAthlete as any).athleteId);
        
        // Also find any other coach athlete records that reference the same original athlete
        const otherCoachRecords = athletes.filter(a => 
          (a as any).athleteId === (selectedAthlete as any).athleteId && 
          a._id !== athleteId
        );
        otherCoachRecords.forEach(record => {
          athleteIdsToSearch.push(record._id);
        });
      } else {
        // If this is an original athlete, also find coach athlete records that reference them
        const coachRecords = athletes.filter(a => 
          (a as any).athleteId === athleteId && 
          a._id !== athleteId
        );
        coachRecords.forEach(record => {
          athleteIdsToSearch.push(record._id);
        });
      }
      
      // Filter equipment by type and any of the relevant athlete IDs
      return equipment.filter(e => {
        const equipmentAthleteId = typeof e.athleteId === 'object' ? e.athleteId._id : (e.athleteId || '');
        return e.type === type && equipmentAthleteId && athleteIdsToSearch.includes(equipmentAthleteId);
      });
    }
    // If no athlete selected, return all equipment of that type
    return equipment.filter(e => e.type === type);
  };

  // Edit handlers
  const handleEditSession = (session: TrainingSessionRecord) => {
    setEditingSessionId(session._id);
    setEditingSessionData({
      trainingType: session.trainingType as 'ski_jumping' | 'ground_training' | 'cross_country',
      date: session.date,
      startTime: session.startTime,
      endTime: session.endTime,
      location: session.location,
      weather: session.weather,
      athleteIds: (session.athleteIds || []).map(id => normalizeAthleteId(id as any)).filter(Boolean) as string[],
    });
    setShowNewTrainingSessionForm(true);
    // Scroll to form after a short delay to ensure it's rendered
    setTimeout(() => {
      editFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleCancelEdit = () => {
    setEditingSessionId(null);
    setEditingJumpId(null);
    setEditingGroundTrainingId(null);
    setEditingCrossCountryId(null);
    setEditingSessionData(null);
    setShowNewTrainingSessionForm(false);
    setShowJumpForm(false);
    setShowGroundTrainingForm(false);
    setNewTrainingSessionData({
      trainingType: 'ski_jumping',
      date: new Date().toISOString().split('T')[0],
      startTime: '',
      endTime: '',
      location: '',
      weather: '',
      athleteIds: [],
    });
    setFormData(prev => ({
      ...prev,
      distance: 0,
      startingGate: 0,
      telemark: false,
      hillSize: '',
      wind: '',
      speed: 0,
      groundTrainingType: 'coordination',
      intensity: 0,
    }));
  };

  const handleUpdateSession = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingSessionId || !editingSessionData) return;
    
    if (editingSessionData.startTime && editingSessionData.endTime) {
      const startTime = new Date(`2000-01-01T${editingSessionData.startTime}`);
      const endTime = new Date(`2000-01-01T${editingSessionData.endTime}`);
      
      if (endTime <= startTime) {
        setError('invalid_end_time');
        return;
      }
    }
    
    if (editingSessionData.athleteIds.length === 0) {
      setError('no_athletes_selected');
      return;
    }
    
    try {
      const sessionData = {
        trainingType: editingSessionData.trainingType,
        date: editingSessionData.date,
        startTime: editingSessionData.startTime,
        endTime: editingSessionData.endTime,
        location: editingSessionData.location,
        weather: editingSessionData.weather || undefined,
        athleteIds: editingSessionData.athleteIds,
      };

      const updatedSession = await trainingSessionAPI.update(editingSessionId, sessionData);
      
      setTrainingSessions(prev => prev.map(session => 
        session._id === editingSessionId ? updatedSession : session
      ));
      
      setEditingSessionId(null);
      setEditingSessionData(null);
      setShowNewTrainingSessionForm(false);
      setError(null);
    } catch (err) {
      console.error('Error updating training session:', err);
      setError('Failed to update training session. Please try again.');
    }
  };

  const handleEditJump = (sessionId: string, jump: JumpRecord) => {
    setEditingJumpId(jump._id);
    (window as any).currentSessionId = sessionId;
    const athleteId = typeof jump.athleteId === 'object' && jump.athleteId !== null && '_id' in jump.athleteId 
      ? (jump.athleteId as any)._id 
      : jump.athleteId;
    const suitId = typeof jump.suitId === 'object' && jump.suitId !== null && '_id' in jump.suitId
      ? (jump.suitId as any)._id
      : jump.suitId;
    const skisId = typeof jump.skisId === 'object' && jump.skisId !== null && '_id' in jump.skisId
      ? (jump.skisId as any)._id
      : jump.skisId;
    const shoesId = jump.shoesId && typeof jump.shoesId === 'object' && jump.shoesId !== null && '_id' in jump.shoesId
      ? (jump.shoesId as any)._id
      : (jump.shoesId || '');
    const bindingsId = jump.bindingsId && typeof jump.bindingsId === 'object' && jump.bindingsId !== null && '_id' in jump.bindingsId
      ? (jump.bindingsId as any)._id
      : (jump.bindingsId || '');
    
    // Format values for rawInputValues (empty when 0, else string with comma as decimal)
    const formatRawValue = (value: number | undefined): string => {
      if (value === undefined || value === null || value === 0) return '';
      return String(value).replace('.', ',');
    };
    
    setFormData(prev => ({
      ...prev,
      athleteId: athleteId,
      suitId: suitId,
      skisId: skisId,
      shoesId: shoesId,
      bindingsId: bindingsId,
      distance: jump.distance || 0,
      startingGate: jump.startingGate || 0,
      telemark: jump.telemark || false,
      hillSize: jump.hillSize || '',
      wind: jump.wind || '',
      speed: jump.speed || 0,
      rating: jump.rating || 0,
    }));
    
    // Set rawInputValues for fields that use decimal input (distance, speed, rating)
    setRawInputValues({
      distance: formatRawValue(jump.distance),
      speed: formatRawValue(jump.speed),
      rating: formatRawValue(jump.rating),
    });
    
    setShowJumpForm(true);
    // Scroll to form after a short delay to ensure it's rendered
    setTimeout(() => {
      const jumpFormElement = document.querySelector('[data-jump-form]');
      jumpFormElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleUpdateJump = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingJumpId) return;
    
    const currentSessionId = (window as any).currentSessionId;
    if (!currentSessionId) return;
    
    try {
      const session = trainingSessions.find(s => s._id === currentSessionId);
      if (!session) return;
      
      const updatedJumps = session.jumps.map(jump => {
        if (jump._id === editingJumpId) {
          return {
            ...jump,
            athleteId: formData.athleteId,
            suitId: formData.suitId || undefined,
            skisId: formData.skisId || undefined,
            shoesId: formData.shoesId || undefined,
            bindingsId: formData.bindingsId || undefined,
            distance: formData.distance,
            startingGate: formData.startingGate,
            telemark: formData.telemark,
            hillSize: formData.hillSize,
            wind: formData.wind,
            speed: formData.speed,
            rating: formData.rating || undefined,
          };
        }
        return jump;
      });
      
      const updatedSession = await trainingSessionAPI.update(currentSessionId, {
        jumps: updatedJumps,
      });
      
      setTrainingSessions(prev => prev.map(session => 
        session._id === currentSessionId ? updatedSession : session
      ));
      
      setEditingJumpId(null);
      setFormData(prev => ({
        ...prev,
        distance: 0,
        startingGate: 0,
        telemark: false,
        hillSize: '',
        wind: '',
        speed: 0,
        rating: 0,
      }));
      setRawInputValues({ distance: '', speed: '', rating: '' });
      setError(null);
    } catch (err) {
      setError('Failed to update jump. Please try again.');
      console.error('Error updating jump:', err);
    }
  };

  const handlePauseSession = async (sessionId: string) => {
    try {
      const updatedSession = await trainingSessionAPI.pause(sessionId);
      setTrainingSessions(prev => prev.map(session => 
        session._id === sessionId ? updatedSession : session
      ));
    } catch (err) {
      setError('Failed to pause session. Please try again.');
      console.error('Error pausing session:', err);
    }
  };

  const handleContinueSession = async (sessionId: string) => {
    try {
      const updatedSession = await trainingSessionAPI.resume(sessionId);
      setTrainingSessions(prev => prev.map(session =>
        session._id === sessionId ? updatedSession : session
      ));
    } catch (err) {
      setError('Failed to continue session. Please try again.');
      console.error('Error continuing session:', err);
    }
  };

  const handleResumeFinishedSession = (sessionId: string) => {
    (window as any).currentSessionId = sessionId;
    setShowJumpForm(true);
    setIsTrainingSessionActive(true);
    setExpandedSessions(prev => new Set(prev).add(sessionId));
    setTimeout(() => jumpFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
  };

  const handleDeleteSession = async (sessionId: string) => {
    if (!window.confirm('Are you sure you want to delete this training session?')) {
      return;
    }
    try {
      await trainingSessionAPI.delete(sessionId);
      setTrainingSessions(prev => prev.filter(session => session._id !== sessionId));
    } catch (err) {
      setError('Failed to delete session. Please try again.');
      console.error('Error deleting session:', err);
    }
  };

  const handleEditGroundTraining = (sessionId: string, groundTraining: GroundTrainingRecord) => {
    setEditingGroundTrainingId(groundTraining._id);
    (window as any).currentSessionId = sessionId;
    const athleteId = typeof groundTraining.athleteId === 'object' && groundTraining.athleteId !== null && '_id' in groundTraining.athleteId 
      ? (groundTraining.athleteId as any)._id 
      : groundTraining.athleteId;
    
    setFormData(prev => ({
      ...prev,
      athleteId: athleteId,
      groundTrainingType: groundTraining.trainingType,
      intensity: groundTraining.intensity || 0,
    }));
    setShowGroundTrainingForm(true);
  };

  const handleUpdateGroundTraining = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingGroundTrainingId) return;
    
    const currentSessionId = (window as any).currentSessionId;
    if (!currentSessionId) return;
    
    try {
      const session = trainingSessions.find(s => s._id === currentSessionId);
      if (!session) return;
      
      const updatedGroundTrainings = session.groundTrainings.map(gt => {
        if (gt._id === editingGroundTrainingId) {
          return {
            ...gt,
            athleteId: formData.athleteId,
            trainingType: formData.groundTrainingType,
            intensity: formData.intensity,
          };
        }
        return gt;
      });
      
      const updatedSession = await trainingSessionAPI.update(currentSessionId, {
        groundTrainings: updatedGroundTrainings,
      });
      
      setTrainingSessions(prev => prev.map(session => 
        session._id === currentSessionId ? updatedSession : session
      ));
      
      setEditingGroundTrainingId(null);
      setShowGroundTrainingForm(false);
      setFormData(prev => ({
        ...prev,
        groundTrainingType: 'coordination',
        intensity: 0,
      }));
      (window as any).currentSessionId = null;
      setError(null);
    } catch (err) {
      setError('Failed to update ground training. Please try again.');
      console.error('Error updating ground training:', err);
    }
  };

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Helper function to check if a session has been recorded (has actual data)
  const isRecordedSession = (session: TrainingSessionRecord): boolean => {
    const hasJumps = !!(session.jumps && session.jumps.length > 0);
    const hasCompetitionJumps = !!(session.competitionJumps && session.competitionJumps.length > 0);
    const hasGroundTrainings = !!(session.groundTrainings && session.groundTrainings.length > 0);
    const hasCrossCountryTrainings = !!(session.crossCountryTrainings && session.crossCountryTrainings.length > 0);
    return hasJumps || hasCompetitionJumps || hasGroundTrainings || hasCrossCountryTrainings;
  };

  const sortedSessions = useMemo(() => {
    // Filter out pre-planned sessions (only show sessions with actual data)
    const recordedSessions = trainingSessions.filter(isRecordedSession);
    return [...recordedSessions].sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'date':
          comparison = new Date(a.date).getTime() - new Date(b.date).getTime();
          break;
        case 'startTime':
          comparison = a.startTime.localeCompare(b.startTime);
          break;
        case 'endTime':
          comparison = a.endTime.localeCompare(b.endTime);
          break;
        case 'trainingType':
          comparison = a.trainingType.localeCompare(b.trainingType);
          break;
        case 'location':
          comparison = a.location.localeCompare(b.location);
          break;
        case 'weather':
          comparison = a.weather.localeCompare(b.weather);
          break;
        case 'category':
          comparison = (a.category || '').localeCompare(b.category || '');
          break;
        case 'athlete': {
          const aAthlete = a.athleteIds.length > 0 ? getAthleteName(a.athleteIds[0]) : '';
          const bAthlete = b.athleteIds.length > 0 ? getAthleteName(b.athleteIds[0]) : '';
          comparison = aAthlete.localeCompare(bAthlete);
          break;
        }
        case 'distance': {
          if (filterAthleteId) {
            // Count only jumps for the specific athlete
            const aJumps = a.jumps.filter(jump => {
              const jumpAthleteId = typeof jump.athleteId === 'object' && jump.athleteId !== null && '_id' in jump.athleteId 
                ? (jump.athleteId as PopulatedAthlete)._id 
                : jump.athleteId as string;
              return jumpAthleteId === filterAthleteId;
            });
            const bJumps = b.jumps.filter(jump => {
              const jumpAthleteId = typeof jump.athleteId === 'object' && jump.athleteId !== null && '_id' in jump.athleteId 
                ? (jump.athleteId as PopulatedAthlete)._id 
                : jump.athleteId as string;
              return jumpAthleteId === filterAthleteId;
            });
            comparison = aJumps.length - bJumps.length;
          } else {
            const aTotalDistance = a.jumps.reduce((sum, jump) => sum + jump.distance, 0);
            const bTotalDistance = b.jumps.reduce((sum, jump) => sum + jump.distance, 0);
            comparison = aTotalDistance - bTotalDistance;
          }
          break;
        }
        case 'gate': {
          const aAvgGate = a.jumps.length > 0 ? a.jumps.reduce((sum, jump) => sum + jump.startingGate, 0) / a.jumps.length : 0;
          const bAvgGate = b.jumps.length > 0 ? b.jumps.reduce((sum, jump) => sum + jump.startingGate, 0) / b.jumps.length : 0;
          comparison = aAvgGate - bAvgGate;
          break;
        }
        case 'telemark':
          if (a.jumps.length > 0 && b.jumps.length > 0) {
            const aJump = a.jumps[0];
            const bJump = b.jumps[0];
            comparison = (aJump.telemark === bJump.telemark) ? 0 : aJump.telemark ? 1 : -1;
          }
          break;
        case 'avgIntensity': {
          const aAvg = getGroundIntensityAverage(a, filterAthleteId, validAthleteIds) || 0;
          const bAvg = getGroundIntensityAverage(b, filterAthleteId, validAthleteIds) || 0;
          comparison = aAvg - bAvg;
          break;
        }
        case 'avgDistance': {
          const aDistances = a.trainingType === 'ski_jumping' && a.jumps 
            ? a.jumps.map(j => j.distance).filter((d): d is number => d !== undefined && d !== null && d > 0)
            : [];
          const bDistances = b.trainingType === 'ski_jumping' && b.jumps 
            ? b.jumps.map(j => j.distance).filter((d): d is number => d !== undefined && d !== null && d > 0)
            : [];
          const aAvg = aDistances.length > 0 ? aDistances.reduce((sum, d) => sum + d, 0) / aDistances.length : 0;
          const bAvg = bDistances.length > 0 ? bDistances.reduce((sum, d) => sum + d, 0) / bDistances.length : 0;
          comparison = aAvg - bAvg;
          break;
        }
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [trainingSessions, sortField, sortDirection, filterAthleteId, validAthleteIds]);

  const sessionInsights = useMemo(() => {
    if (!Array.isArray(trainingSessions) || trainingSessions.length === 0) {
      return {
        totalSessions: 0,
        totalJumps: 0,
        totalGroundRecords: 0,
        avgGroundIntensity: null as number | null,
        activeAthletes: 0,
        last30DaysSessions: 0,
        topTrainingTypeLabel: '—',
      };
    }

    const recordedSessions = trainingSessions.filter(session => {
      return (
        (session.jumps && session.jumps.length > 0) ||
        (session.competitionJumps && session.competitionJumps.length > 0) ||
        (session.groundTrainings && session.groundTrainings.length > 0) ||
        (session.crossCountryTrainings && session.crossCountryTrainings.length > 0)
      );
    });

    const totalSessions = recordedSessions.length;
    const totalJumps = recordedSessions.reduce(
      (sum, session) => sum + (session.jumps?.length || 0) + (session.competitionJumps?.length || 0),
      0
    );
    const totalGroundRecords = recordedSessions.reduce(
      (sum, session) => sum + (session.groundTrainings?.length || 0),
      0
    );

    // Calculate ground intensity average - filter by specific athlete if filterAthleteId is provided
    const groundIntensityTotals = recordedSessions.reduce(
      (acc, session) => {
        (session.groundTrainings || []).forEach(record => {
          // If filterAthleteId is provided, only include records for that athlete
          if (filterAthleteId && validAthleteIds.length > 0) {
            const recordAthleteId = (typeof record.athleteId === 'object' && record.athleteId !== null && '_id' in record.athleteId)
              ? (record.athleteId as any)._id.toString()
              : (record.athleteId as string).toString();
            
            // Only include if the athlete ID is in the valid list
            if (validAthleteIds.includes(recordAthleteId)) {
              acc.total += record.intensity || 0;
              acc.count += 1;
            }
          } else if (filterAthleteId) {
            // Fallback to direct comparison if validAthleteIds not provided
            const recordAthleteId = (typeof record.athleteId === 'object' && record.athleteId !== null && '_id' in record.athleteId)
              ? (record.athleteId as any)._id 
              : record.athleteId;
            const normalizedRecordAthleteId = normalizeAthleteId(recordAthleteId as any);
            const normalizedFilterAthleteId = normalizeAthleteId(filterAthleteId as any);
            
            // Only include if the athlete IDs match
            if (normalizedRecordAthleteId === normalizedFilterAthleteId) {
              acc.total += record.intensity || 0;
              acc.count += 1;
            }
          } else {
            // If no filter, include all records (coach view)
            acc.total += record.intensity || 0;
            acc.count += 1;
          }
        });
        return acc;
      },
      { total: 0, count: 0 }
    );

    const avgGroundIntensity =
      groundIntensityTotals.count > 0 ? groundIntensityTotals.total / groundIntensityTotals.count : null;

    const activeAthletesSet = new Set<string>();
    recordedSessions.forEach(session => {
      (session.athleteIds || []).forEach(athleteRef => {
        const normalized = normalizeAthleteId(athleteRef as any);
        if (normalized) {
          activeAthletesSet.add(normalized);
        }
      });
    });

    const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
    const now = Date.now();
    const last30DaysSessions = recordedSessions.filter(session => {
      if (!session.date) return false;
      const sessionDate = new Date(session.date);
      if (Number.isNaN(sessionDate.getTime())) return false;
      return now - sessionDate.getTime() <= THIRTY_DAYS;
    }).length;

    const typeLabelMap: Record<string, string> = {
      ski_jumping: 'Ski jumping',
      ground_training: 'Ground training',
      cross_country: 'Cross country',
      competition: 'Competition',
      banana_cup: 'Banana Cup',
    };

    const typeCounts = recordedSessions.reduce<Record<string, number>>((counts, session) => {
      const key = session.trainingType || 'unknown';
      counts[key] = (counts[key] || 0) + 1;
      return counts;
    }, {});

    const topTrainingTypeLabel =
      Object.keys(typeCounts).length === 0
        ? '—'
        : (typeLabelMap[
            Object.keys(typeCounts).reduce((maxKey, key) =>
              typeCounts[key] > (typeCounts[maxKey] || 0) ? key : maxKey
            )
          ] || '—');

    return {
      totalSessions,
      totalJumps,
      totalGroundRecords,
      avgGroundIntensity,
      activeAthletes: activeAthletesSet.size,
      last30DaysSessions,
      topTrainingTypeLabel,
    };
  }, [trainingSessions, filterAthleteId, validAthleteIds]);

  // Helper functions to filter sessions by type - only show sessions that have been started (have records)
  const getSkiJumpingSessions = () => {
    return sortedSessions.filter(session => 
      session.trainingType === 'ski_jumping' && 
      session.jumps && session.jumps.length > 0
    );
  };

  const getGroundTrainingSessions = () => {
    return sortedSessions.filter(session => 
      session.trainingType === 'ground_training' && 
      session.groundTrainings && session.groundTrainings.length > 0
    );
  };

  const getCrossCountrySessions = () => {
    return sortedSessions.filter(session => 
      session.trainingType === 'cross_country' && 
      session.crossCountryTrainings && session.crossCountryTrainings.length > 0
    );
  };


  // Component to render a single session row
  const SessionRow = ({ session, columns }: { session: TrainingSessionRecord; columns: { field: SortField; label: string; width?: string }[] }) => {
      const getCellValue = (field: SortField) => {
    switch (field) {
      case 'date':
        return session.date;
      case 'location':
        return session.location;
      case 'weather':
        if (!session.weather) return '-';
        const weatherValue = session.weather === 'indoor' ? 'INDOOR' : session.weather.replace(/[^a-zA-Z\s]/g, '').toUpperCase();
        return weatherValue;
      case 'category':
        return session.category ? session.category.replace(/[^a-zA-Z\s]/g, '').toUpperCase() : '-';
      case 'athlete':
        // Only show athletes who actually participated (have data in the session)
        // Extract unique athlete IDs from actual data, not from pre-planned athleteIds
        let participatingAthleteIds: string[] = [];
        
        if (session.trainingType === 'ski_jumping' && session.jumps && session.jumps.length > 0) {
          // Get unique athlete IDs from jumps (each athlete appears only once)
          const athleteIdSet = new Set<string>();
          session.jumps.forEach(jump => {
            const id = normalizeAthleteId(jump.athleteId as any);
            if (id) athleteIdSet.add(id);
          });
          participatingAthleteIds = Array.from(athleteIdSet);
        } else if (session.trainingType === 'ground_training' && session.groundTrainings && session.groundTrainings.length > 0) {
          // Get unique athlete IDs from ground trainings (each athlete appears only once)
          const athleteIdSet = new Set<string>();
          session.groundTrainings.forEach(gt => {
            const id = normalizeAthleteId(gt.athleteId as any);
            if (id) athleteIdSet.add(id);
          });
          participatingAthleteIds = Array.from(athleteIdSet);
        } else if (session.trainingType === 'cross_country' && session.crossCountryTrainings && session.crossCountryTrainings.length > 0) {
          // Get unique athlete IDs from cross country trainings (each athlete appears only once)
          const athleteIdSet = new Set<string>();
          session.crossCountryTrainings.forEach(cc => {
            const id = normalizeAthleteId(cc.athleteId as any);
            if (id) athleteIdSet.add(id);
          });
          participatingAthleteIds = Array.from(athleteIdSet);
        }
        
        // If no data exists yet, return empty string (don't show planned athletes)
        if (participatingAthleteIds.length === 0) {
          return '-';
        }
        
        // Return unique athlete names, each athlete shown only once
        return participatingAthleteIds.map(id => getAthleteName(id)).join(', ');
      case 'distance':
        if (session.trainingType === 'ski_jumping') {
          if (filterAthleteId) {
            // Count only jumps for the specific athlete
            // Use validAthleteIds if available (for jumper dashboard), otherwise fall back to direct comparison
            if (validAthleteIds.length > 0) {
              return session.jumps.filter(jump => {
                const jumpAthleteId = typeof jump.athleteId === 'object' && jump.athleteId !== null && '_id' in jump.athleteId 
                  ? (jump.athleteId as PopulatedAthlete)._id.toString()
                  : (jump.athleteId as string).toString();
                return validAthleteIds.includes(jumpAthleteId);
              }).length;
            } else {
              // Fallback to direct comparison
              return session.jumps.filter(jump => {
                const jumpAthleteId = typeof jump.athleteId === 'object' && jump.athleteId !== null && '_id' in jump.athleteId 
                  ? (jump.athleteId as PopulatedAthlete)._id 
                  : jump.athleteId as string;
                return jumpAthleteId === filterAthleteId;
              }).length;
            }
          }
          return session.jumps.length;
        } else if (session.trainingType === 'ground_training') {
          if (filterAthleteId) {
            // Count only ground trainings for the specific athlete
            // Use validAthleteIds if available, otherwise fall back to direct comparison
            const idsToMatch = validAthleteIds.length > 0 ? validAthleteIds : [filterAthleteId];
            
            return session.groundTrainings?.filter(gt => {
              let gtAthleteId: string;
              
              if (gt.athleteId === null || gt.athleteId === undefined) {
                return false;
              }
              
              if (typeof gt.athleteId === 'object' && gt.athleteId !== null) {
                const athleteObj = gt.athleteId as any;
                if ('_id' in athleteObj && athleteObj._id) {
                  gtAthleteId = String(athleteObj._id);
                } else {
                  gtAthleteId = String(athleteObj._id || athleteObj);
                }
              } else {
                gtAthleteId = String(gt.athleteId);
              }
              
              const normalizedIdsToMatch = idsToMatch.map(id => String(id));
              return normalizedIdsToMatch.includes(gtAthleteId);
            }).length || 0;
          }
          return session.groundTrainings?.length || 0;
        } else if (session.trainingType === 'cross_country') {
          if (filterAthleteId) {
            // Count only cross country trainings for the specific athlete
            return session.crossCountryTrainings?.filter(cc => {
              const ccAthleteId = typeof cc.athleteId === 'object' && cc.athleteId !== null && '_id' in cc.athleteId 
                ? (cc.athleteId as PopulatedAthlete)._id 
                : cc.athleteId as string;
              return ccAthleteId === filterAthleteId;
            }).length || 0;
          }
          return session.crossCountryTrainings?.length || 0;
        }
        return 0;
      case 'avgIntensity': {
        const avg = getGroundIntensityAverage(session, filterAthleteId, validAthleteIds);
        return avg !== null ? `${avg.toFixed(1)}/10` : '-';
      }
      case 'avgDistance': {
        if (session.trainingType === 'ski_jumping' && session.jumps && session.jumps.length > 0) {
          // Filter jumps by athlete if filterAthleteId is provided
          let jumpsToUse = session.jumps;
          if (filterAthleteId) {
            if (validAthleteIds.length > 0) {
              jumpsToUse = session.jumps.filter(jump => {
                const jumpAthleteId = typeof jump.athleteId === 'object' && jump.athleteId !== null && '_id' in jump.athleteId 
                  ? (jump.athleteId as PopulatedAthlete)._id.toString()
                  : (jump.athleteId as string).toString();
                return validAthleteIds.includes(jumpAthleteId);
              });
            } else {
              jumpsToUse = session.jumps.filter(jump => {
                const jumpAthleteId = typeof jump.athleteId === 'object' && jump.athleteId !== null && '_id' in jump.athleteId 
                  ? (jump.athleteId as PopulatedAthlete)._id 
                  : jump.athleteId as string;
                return jumpAthleteId === filterAthleteId;
              });
            }
          }
          
          const distances = jumpsToUse
            .map(jump => jump.distance)
            .filter((d): d is number => d !== undefined && d !== null && d > 0);
          if (distances.length === 0) return '-';
          const avg = distances.reduce((sum, d) => sum + d, 0) / distances.length;
          return `${avg.toFixed(1)}m`;
        }
        return '-';
      }
      default:
        return '-';
    }
  };

    const isMobileDevice = window.innerWidth <= 768;
    return (
      <React.Fragment key={session._id}>
        <TableRow>
          <TableCell style={{ fontSize: '0.7rem', padding: isMobileDevice ? '0.25rem 0.1rem' : '0.25rem', width: isMobileDevice ? '20px' : '30px' }}>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                
                // Store current scroll position
                scrollPositionRef.current = window.scrollY;
                
                setExpandedSessions(prev => {
                  const newSet = new Set(prev);
                  if (newSet.has(session._id)) {
                    newSet.delete(session._id);
                  } else {
                    newSet.add(session._id);
                  }
                  return newSet;
                });
                
                // Restore scroll position after state update
                setTimeout(() => {
                  window.scrollTo(0, scrollPositionRef.current);
                }, 0);
              }}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '1rem',
                color: 'var(--text-gray)',
                padding: 0,
                margin: 0
              }}
            >
              {expandedSessions.has(session._id) ? '▼' : '▶'}
            </button>
          </TableCell>
          {columns.map(column => (
            <TableCell key={column.field} style={{ 
              fontSize: '0.7rem', 
              padding: '0.25rem', 
              textAlign: 'center',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: isMobileDevice ? '120px' : 'none'
            }}>
              {getCellValue(column.field)}
            </TableCell>
          ))}
          <TableCell style={{ fontSize: '0.7rem', padding: '0.25rem', textAlign: 'center' }}>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', alignItems: 'center' }}>
              {session.trainingType === 'ski_jumping' && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const isCurrentAndActive = (window as any).currentSessionId === session._id && !session.isPaused;
                    if (isCurrentAndActive) {
                      handlePauseSession(session._id);
                    } else if (session.isPaused) {
                      handleContinueSession(session._id);
                    } else {
                      handleResumeFinishedSession(session._id);
                    }
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    color: '#333',
                    padding: '0.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title={(window as any).currentSessionId === session._id && !session.isPaused ? 'Pause session' : (session.isPaused ? 'Continue session' : 'Continue / Add more jumps to this session')}
                >
                  <span style={{ fontSize: '0.75rem', color: '#333' }}>
                    {(window as any).currentSessionId === session._id && !session.isPaused ? '⏸' : '▶'}
                  </span>
                </button>
              )}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleEditSession(session);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  color: '#666',
                  padding: '0.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Edit session"
              >
                <span style={{ fontSize: '0.75rem', color: '#333' }}>✎</span>
              </button>
              {!isTrainingSessionActive && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDeleteSession(session._id);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    color: '#dc3545',
                    padding: '0.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title="Delete session"
                >
                  <span style={{ fontSize: '0.75rem' }}>🗑️</span>
                </button>
              )}
            </div>
          </TableCell>
        </TableRow>
        {expandedSessions.has(session._id) && (
          <TableRow>
            <TableCell colSpan={columns.length + 2} style={{ padding: '0', border: 'none' }}>
              <div style={{ 
                marginLeft: '30px', 
                background: 'rgba(0,0,0,0.02)',
                overflow: 'hidden',
                transition: 'all 0.2s ease-in-out'
              }}>
                {session.trainingType === 'ski_jumping' && session.jumps && session.jumps.length > 0 && (
                  <table style={{ width: '100%', fontSize: '0.65rem' }}>
                    <thead>
                      <tr>
                        <th style={{ padding: '0.25rem', textAlign: 'left' }}>Jump #</th>
                        <th 
                          style={{ padding: '0.25rem', textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => {
                            if (jumpSortField === 'athlete') {
                              setJumpSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
                            } else {
                              setJumpSortField('athlete');
                              setJumpSortDirection('asc');
                            }
                          }}
                        >
                          Athlete {jumpSortField === 'athlete' && (jumpSortDirection === 'asc' ? '↑' : '↓')}
                        </th>
                        <th 
                          style={{ padding: '0.25rem', textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => {
                            if (jumpSortField === 'gate') {
                              setJumpSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
                            } else {
                              setJumpSortField('gate');
                              setJumpSortDirection('asc');
                            }
                          }}
                        >
                          Gate {jumpSortField === 'gate' && (jumpSortDirection === 'asc' ? '↑' : '↓')}
                        </th>
                        <th 
                          style={{ padding: '0.25rem', textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => {
                            if (jumpSortField === 'distance') {
                              setJumpSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
                            } else {
                              setJumpSortField('distance');
                              setJumpSortDirection('asc');
                            }
                          }}
                        >
                          Distance {jumpSortField === 'distance' && (jumpSortDirection === 'asc' ? '↑' : '↓')}
                        </th>
                        <th 
                          style={{ padding: '0.25rem', textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => {
                            if (jumpSortField === 'wind') {
                              setJumpSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
                            } else {
                              setJumpSortField('wind');
                              setJumpSortDirection('asc');
                            }
                          }}
                        >
                          Wind {jumpSortField === 'wind' && (jumpSortDirection === 'asc' ? '↑' : '↓')}
                        </th>
                        <th 
                          style={{ padding: '0.25rem', textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => {
                            if (jumpSortField === 'speed') {
                              setJumpSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
                            } else {
                              setJumpSortField('speed');
                              setJumpSortDirection('asc');
                            }
                          }}
                        >
                          Speed {jumpSortField === 'speed' && (jumpSortDirection === 'asc' ? '↑' : '↓')}
                        </th>
                        <th 
                          style={{ padding: '0.25rem', textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => {
                            if (jumpSortField === 'telemark') {
                              setJumpSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
                            } else {
                              setJumpSortField('telemark');
                              setJumpSortDirection('asc');
                            }
                          }}
                        >
                          Telemark {jumpSortField === 'telemark' && (jumpSortDirection === 'asc' ? '↑' : '↓')}
                        </th>
                        <th 
                          style={{ padding: '0.25rem', textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => {
                            if (jumpSortField === 'hillSize') {
                              setJumpSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
                            } else {
                              setJumpSortField('hillSize');
                              setJumpSortDirection('asc');
                            }
                          }}
                        >
                          Hill Size {jumpSortField === 'hillSize' && (jumpSortDirection === 'asc' ? '↑' : '↓')}
                        </th>
                        <th style={{ padding: '0.25rem', textAlign: 'center', fontSize: '0.65rem', fontWeight: 'bold' }}>Rating</th>
                        <th style={{ padding: '0.25rem', textAlign: 'center', fontSize: '0.65rem', fontWeight: 'bold' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        // Filter jumps by athlete if filterAthleteId is provided
                        let filteredJumps = session.jumps;
                        if (filterAthleteId && validAthleteIds.length > 0) {
                          filteredJumps = session.jumps.filter(jump => {
                            const jumpAthleteId = typeof jump.athleteId === 'object' && jump.athleteId !== null && '_id' in jump.athleteId 
                              ? (jump.athleteId as PopulatedAthlete)._id.toString()
                              : (jump.athleteId as string).toString();
                            return validAthleteIds.includes(jumpAthleteId);
                          });
                        } else if (filterAthleteId) {
                          // Fallback to direct comparison if validAthleteIds not provided
                          filteredJumps = session.jumps.filter(jump => {
                            const jumpAthleteId = typeof jump.athleteId === 'object' && jump.athleteId !== null && '_id' in jump.athleteId 
                              ? (jump.athleteId as PopulatedAthlete)._id 
                              : jump.athleteId as string;
                            return jumpAthleteId === filterAthleteId;
                          });
                        }

                        // Group jumps by athlete and calculate jump numbers
                        const athleteJumpCounts = new Map<string, number>();
                        const jumpsWithNumbers = filteredJumps.map(jump => {
                          // Extract athleteId consistently as string for comparison
                          const athleteId = typeof jump.athleteId === 'object' && jump.athleteId !== null && '_id' in jump.athleteId 
                            ? (jump.athleteId as PopulatedAthlete)._id.toString()
                            : (jump.athleteId as string).toString();
                          const currentCount = athleteJumpCounts.get(athleteId) || 0;
                          athleteJumpCounts.set(athleteId, currentCount + 1);
                          const jumpNumber = currentCount + 1;
                          return { ...jump, jumpNumber, athleteId };
                        });

                        // Sort jumps based on selected field
                        const sortedJumps = [...jumpsWithNumbers].sort((a, b) => {
                          let comparison = 0;
                          switch (jumpSortField) {
                            case 'athlete':
                              comparison = getAthleteName(a.athleteId).localeCompare(getAthleteName(b.athleteId));
                              break;
                            case 'gate':
                              comparison = (a.startingGate || 0) - (b.startingGate || 0);
                              break;
                            case 'distance':
                              comparison = (a.distance || 0) - (b.distance || 0);
                              break;
                            case 'wind':
                              comparison = (a.wind || '').localeCompare(b.wind || '');
                              break;
                            case 'speed':
                              comparison = (a.speed || 0) - (b.speed || 0);
                              break;
                            case 'telemark':
                              comparison = (a.telemark === b.telemark) ? 0 : a.telemark ? 1 : -1;
                              break;
                            case 'hillSize':
                              comparison = (a.hillSize || '').localeCompare(b.hillSize || '');
                              break;
                            default:
                              // Default sort by jump number (original order)
                              comparison = a.jumpNumber - b.jumpNumber;
                          }
                          return jumpSortDirection === 'asc' ? comparison : -comparison;
                        });

                        // Show empty state if no jumps found after filtering
                        if (sortedJumps.length === 0) {
                          return (
                            <tr>
                              <td colSpan={13} style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-gray)', opacity: 0.7 }}>
                                No jumps recorded for this athlete in this session.
                              </td>
                            </tr>
                          );
                        }

                        return sortedJumps.map(jump => (
                          <tr key={jump._id}>
                            <td style={{ padding: '0.25rem', fontWeight: 'bold' }}>#{jump.jumpNumber}</td>
                            <td style={{ padding: '0.25rem' }}>{getAthleteName(jump.athleteId)}</td>
                            <td style={{ padding: '0.25rem' }}>{jump.startingGate || '-'}</td>
                            <td style={{ padding: '0.25rem' }}>{jump.distance !== undefined && jump.distance !== null && !isNaN(jump.distance) ? `${jump.distance} m` : '-'}</td>
                            <td style={{ padding: '0.25rem' }}>{jump.wind || '-'}</td>
                            <td style={{ padding: '0.25rem' }}>{jump.speed !== undefined && jump.speed !== null && !isNaN(jump.speed) ? `${jump.speed} km/h` : '-'}</td>
                            <td style={{ padding: '0.25rem' }}>{jump.telemark ? 'Yes' : 'No'}</td>
                            <td style={{ padding: '0.25rem' }}>{jump.hillSize ? `HS ${jump.hillSize}` : '-'}</td>
                            <td style={{ padding: '0.25rem', textAlign: 'center' }}>
                              {jump.rating !== undefined && jump.rating !== null && jump.rating > 0 
                                ? `${jump.rating}/10` 
                                : '-'}
                            </td>
                            <td style={{ padding: '0.25rem', textAlign: 'center' }}>
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleEditJump(session._id, jump);
                                }}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontSize: '1rem',
                                  color: '#666',
                                  padding: '0.25rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  margin: '0 auto'
                                }}
                                title="Edit jump"
                              >
                                <span style={{ fontSize: '0.75rem', color: '#333' }}>✎</span>
                              </button>
                            </td>
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                )}
                
                {session.trainingType === 'ground_training' && session.groundTrainings && session.groundTrainings.length > 0 && (
                  <table style={{ width: '100%', fontSize: '0.65rem' }}>
                    <thead>
                      <tr>
                        <th 
                          style={{ padding: '0.25rem', textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => {
                            if (groundTrainingSortField === 'athlete') {
                              setGroundTrainingSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
                            } else {
                              setGroundTrainingSortField('athlete');
                              setGroundTrainingSortDirection('asc');
                            }
                          }}
                        >
                          Athlete {groundTrainingSortField === 'athlete' && (groundTrainingSortDirection === 'asc' ? '↑' : '↓')}
                        </th>
                        <th 
                          style={{ padding: '0.25rem', textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => {
                            if (groundTrainingSortField === 'trainingType') {
                              setGroundTrainingSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
                            } else {
                              setGroundTrainingSortField('trainingType');
                              setGroundTrainingSortDirection('asc');
                            }
                          }}
                        >
                          Training Type {groundTrainingSortField === 'trainingType' && (groundTrainingSortDirection === 'asc' ? '↑' : '↓')}
                        </th>
                        <th 
                          style={{ padding: '0.25rem', textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => {
                            if (groundTrainingSortField === 'intensity') {
                              setGroundTrainingSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
                            } else {
                              setGroundTrainingSortField('intensity');
                              setGroundTrainingSortDirection('asc');
                            }
                          }}
                        >
                          Intensity {groundTrainingSortField === 'intensity' && (groundTrainingSortDirection === 'asc' ? '↑' : '↓')}
                        </th>
                        <th style={{ padding: '0.25rem', textAlign: 'center', fontSize: '0.65rem', fontWeight: 'bold' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        // Filter ground trainings by athlete if filterAthleteId is provided
                        let filteredGroundTrainings = session.groundTrainings || [];
                        
                        if (filterAthleteId) {
                          // Always use validAthleteIds if available, otherwise fall back to direct comparison
                          const idsToMatch = validAthleteIds.length > 0 ? validAthleteIds : [filterAthleteId];
                          
                          // Debug: Log the first session to understand the data structure
                          if (session._id && session.groundTrainings && session.groundTrainings.length > 0) {
                            console.log('=== Ground Training Filter Debug ===');
                            console.log('Session ID:', session._id);
                            console.log('Session Date:', session.date);
                            console.log('Filter Athlete ID:', filterAthleteId);
                            console.log('Valid Athlete IDs:', validAthleteIds);
                            console.log('IDs to Match:', idsToMatch);
                            console.log('Total Ground Trainings:', session.groundTrainings.length);
                            session.groundTrainings.forEach((gt: any, idx: number) => {
                              console.log(`Ground Training ${idx + 1}:`, {
                                _id: gt._id,
                                athleteId: gt.athleteId,
                                athleteIdType: typeof gt.athleteId,
                                athleteIdIsObject: typeof gt.athleteId === 'object' && gt.athleteId !== null,
                                has_id: typeof gt.athleteId === 'object' && gt.athleteId !== null && '_id' in gt.athleteId,
                                trainingType: gt.trainingType,
                                intensity: gt.intensity
                              });
                            });
                          }
                          
                          filteredGroundTrainings = filteredGroundTrainings.filter(gt => {
                            let gtAthleteId: string;
                            
                            // Handle all possible formats of athleteId
                            if (gt.athleteId === null || gt.athleteId === undefined) {
                              console.log('Skipping record - no athleteId');
                              return false; // Skip records with no athleteId
                            }
                            
                            if (typeof gt.athleteId === 'object' && gt.athleteId !== null) {
                              // Populated athlete object - extract _id
                              const athleteObj = gt.athleteId as any;
                              if ('_id' in athleteObj && athleteObj._id) {
                                gtAthleteId = String(athleteObj._id);
                              } else {
                                // Try to get _id from the object directly
                                gtAthleteId = String(athleteObj._id || athleteObj);
                              }
                            } else {
                              // String or other primitive - convert to string
                              gtAthleteId = String(gt.athleteId);
                            }
                            
                            // Normalize all IDs to strings for comparison
                            const normalizedIdsToMatch = idsToMatch.map(id => String(id));
                            
                            const isMatch = normalizedIdsToMatch.includes(gtAthleteId);
                            
                            console.log('Filter Check:', {
                              extractedAthleteId: gtAthleteId,
                              normalizedIdsToMatch,
                              isMatch,
                              trainingType: gt.trainingType,
                              intensity: gt.intensity
                            });
                            
                            // Check if this athlete ID is in our valid list
                            return isMatch;
                          });
                          
                          console.log('Filtered Result:', {
                            before: session.groundTrainings?.length || 0,
                            after: filteredGroundTrainings.length
                          });
                        }

                        // Sort ground trainings
                        const sortedTrainings = [...filteredGroundTrainings].sort((a, b) => {
                          let comparison = 0;
                          switch (groundTrainingSortField) {
                            case 'athlete':
                              comparison = getAthleteName(a.athleteId).localeCompare(getAthleteName(b.athleteId));
                              break;
                            case 'trainingType':
                              comparison = a.trainingType.localeCompare(b.trainingType);
                              break;
                            case 'intensity':
                              comparison = a.intensity - b.intensity;
                              break;
                            default:
                              comparison = 0;
                          }
                          return groundTrainingSortDirection === 'asc' ? comparison : -comparison;
                        });

                        return sortedTrainings.map(groundTraining => (
                          <tr key={groundTraining._id}>
                            <td style={{ padding: '0.25rem' }}>{getAthleteName(groundTraining.athleteId)}</td>
                            <td style={{ padding: '0.25rem' }}>{groundTraining.trainingType.replace('_', ' ').toUpperCase()}</td>
                            <td style={{ padding: '0.25rem' }}>{groundTraining.intensity}/10</td>
                            <td style={{ padding: '0.25rem', textAlign: 'center' }}>
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleEditGroundTraining(session._id, groundTraining);
                                }}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontSize: '1rem',
                                  color: '#666',
                                  padding: '0.25rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  margin: '0 auto'
                                }}
                                title="Edit ground training"
                              >
                                <span style={{ fontSize: '0.75rem', color: '#333' }}>✎</span>
                              </button>
                            </td>
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                )}
                
                {session.trainingType === 'cross_country' && session.crossCountryTrainings && session.crossCountryTrainings.length > 0 && (
                  <table style={{ width: '100%', fontSize: '0.65rem' }}>
                    <thead>
                      <tr>
                        <th 
                          style={{ padding: '0.25rem', textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => {
                            if (crossCountrySortField === 'athlete') {
                              setCrossCountrySortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
                            } else {
                              setCrossCountrySortField('athlete');
                              setCrossCountrySortDirection('asc');
                            }
                          }}
                        >
                          Athlete {crossCountrySortField === 'athlete' && (crossCountrySortDirection === 'asc' ? '↑' : '↓')}
                        </th>
                        <th style={{ padding: '0.25rem', textAlign: 'left' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        // Filter cross country trainings by athlete if filterAthleteId is provided
                        let filteredCrossCountryTrainings = session.crossCountryTrainings;
                        if (filterAthleteId) {
                          filteredCrossCountryTrainings = session.crossCountryTrainings.filter(cc => {
                            const ccAthleteId = typeof cc.athleteId === 'object' && cc.athleteId !== null && '_id' in cc.athleteId 
                              ? (cc.athleteId as PopulatedAthlete)._id 
                              : cc.athleteId as string;
                            return ccAthleteId === filterAthleteId;
                          });
                        }

                        // Sort cross country trainings
                        const sortedTrainings = [...filteredCrossCountryTrainings].sort((a, b) => {
                          let comparison = 0;
                          if (crossCountrySortField === 'athlete') {
                            comparison = getAthleteName(a.athleteId).localeCompare(getAthleteName(b.athleteId));
                          }
                          return crossCountrySortDirection === 'asc' ? comparison : -comparison;
                        });

                        return sortedTrainings.map(crossCountryTraining => (
                          <tr key={crossCountryTraining._id}>
                            <td style={{ padding: '0.25rem' }}>{getAthleteName(crossCountryTraining.athleteId)}</td>
                            <td style={{ padding: '0.25rem', fontStyle: 'italic', color: '#888' }}>Data collection pending</td>
                            <td style={{ padding: '0.25rem', textAlign: 'center' }}>
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  // Cross country editing can be added later when data structure is defined
                                }}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'not-allowed',
                                  fontSize: '1rem',
                                  color: '#ccc',
                                  padding: '0.25rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  margin: '0 auto'
                                }}
                                title="Edit cross country (coming soon)"
                                disabled
                              >
                                <span style={{ fontSize: '0.75rem', color: '#ccc' }}>✎</span>
                              </button>
                            </td>
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                )}
                
                {/* Additional Session Info */}
                {session.trainingType === 'ski_jumping' && session.jumps && session.jumps.length > 0 && (
                  <>
                    {/* Coach's Notes - Above Session Summary */}
                    {session.coachDescription && (
                      <div style={{ 
                        padding: '1rem', 
                        marginTop: '1rem', 
                        background: 'rgba(255, 150, 0, 0.05)',
                        borderRadius: '0.5rem',
                        border: '1px solid rgba(255, 150, 0, 0.2)',
                        marginBottom: '0.75rem'
                      }}>
                        <strong style={{ fontSize: '0.75rem', color: '#666', display: 'block', marginBottom: '0.5rem' }}>Coach's Notes:</strong>
                        <p style={{ fontSize: '0.7rem', color: '#333', margin: 0, lineHeight: '1.4' }}>
                          {session.coachDescription}
                        </p>
                      </div>
                    )}
                    
                    {/* Session Summary */}
                    <div style={{ 
                      padding: '1rem', 
                      marginTop: session.coachDescription ? '0' : '1rem', 
                      background: 'rgba(255, 150, 0, 0.05)',
                      borderRadius: '0.5rem',
                      border: '1px solid rgba(255, 150, 0, 0.2)'
                    }}>
                      <h4 style={{ 
                        fontSize: '0.9rem', 
                        fontWeight: 700, 
                        color: 'var(--accent-orange)',
                        marginBottom: '0.75rem',
                        fontFamily: 'Poppins, sans-serif'
                      }}>
                        Session Summary
                      </h4>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem' }}>
                        {(() => {
                        // Calculate stats per athlete
                        // For jumpers, filter to show only their own data
                        const currentAthleteId = user?.role === 'athlete' && user?.athleteId 
                          ? normalizeAthleteId(user.athleteId as any)
                          : filterAthleteId ? normalizeAthleteId(filterAthleteId as any) : null;
                        
                        const athleteStats = new Map<string, {
                          ratings: number[];
                          distances: number[];
                          speeds: number[];
                        }>();
                        
                        session.jumps.forEach(jump => {
                          const athleteId = normalizeAthleteId(jump.athleteId as any);
                          // Filter by current athlete if viewing as jumper or filtered view
                          if (currentAthleteId && athleteId !== currentAthleteId) {
                            return; // Skip jumps from other athletes
                          }
                          if (!athleteStats.has(athleteId)) {
                            athleteStats.set(athleteId, { ratings: [], distances: [], speeds: [] });
                          }
                          const stats = athleteStats.get(athleteId)!;
                          if (jump.rating) stats.ratings.push(jump.rating);
                          if (jump.distance) stats.distances.push(jump.distance);
                          if (jump.speed) stats.speeds.push(jump.speed);
                        });
                        
                        const athleteStatsArray = Array.from(athleteStats.entries()).map(([athleteId, stats]) => ({
                          athleteId,
                          athleteName: getAthleteName(athleteId),
                          avgRating: stats.ratings.length > 0 
                            ? (stats.ratings.reduce((a, b) => a + b, 0) / stats.ratings.length).toFixed(1)
                            : null,
                          longestDistance: stats.distances.length > 0 
                            ? Math.max(...stats.distances).toFixed(1)
                            : null,
                          avgDistance: stats.distances.length > 0
                            ? (stats.distances.reduce((a, b) => a + b, 0) / stats.distances.length).toFixed(1)
                            : null,
                          avgSpeed: stats.speeds.length > 0
                            ? (stats.speeds.reduce((a, b) => a + b, 0) / stats.speeds.length).toFixed(1)
                            : null
                        }));
                        
                        return athleteStatsArray.map(({ athleteId, athleteName, avgRating, longestDistance, avgDistance, avgSpeed }) => (
                          <div key={athleteId} style={{ 
                            padding: '0.5rem',
                            background: 'rgba(255, 255, 255, 0.5)',
                            borderRadius: '0.25rem'
                          }}>
                            <strong style={{ fontSize: '0.75rem', color: 'var(--accent-orange)' }}>{athleteName}:</strong>
                            <div style={{ fontSize: '0.65rem', color: '#666', marginTop: '0.25rem', lineHeight: '1.5' }}>
                              {avgRating && <div>Avg Rating: {avgRating}/10</div>}
                              {longestDistance && <div>Longest: {longestDistance}m</div>}
                              {avgDistance && <div>Avg Distance: {avgDistance}m</div>}
                              {avgSpeed && <div>Avg Speed: {avgSpeed} km/h</div>}
                            </div>
                          </div>
                        ));
                      })()}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </TableCell>
          </TableRow>
        )}
      </React.Fragment>
    );
  };

  // Component to render a training sessions table
  const TrainingSessionsTable = ({ 
    title, 
    sessions, 
    columns,
    expandedMonths,
    setExpandedMonths
  }: { 
    title: string; 
    sessions: TrainingSessionRecord[]; 
    columns: { field: SortField; label: string; width?: string }[];
    expandedMonths: Set<string>;
    setExpandedMonths: React.Dispatch<React.SetStateAction<Set<string>>>;
  }) => {
    const groupedSessions = groupSessionsByMonth(sessions);
    // Filter out weather column on mobile
    const filteredColumns = isMobile ? columns.filter(col => col.field !== 'weather') : columns;
    const totalColumns = filteredColumns.length + 2; // expander + actions
    
    // State for scroll indicator
    const [showScrollIndicator, setShowScrollIndicator] = useState(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    
    // Check if table can scroll horizontally
    useEffect(() => {
      if (!isMobile || !scrollContainerRef.current) return;
      
      const checkScroll = () => {
        const container = scrollContainerRef.current;
        if (container) {
          const canScroll = container.scrollWidth > container.clientWidth;
          const isScrolledToEnd = container.scrollLeft + container.clientWidth >= container.scrollWidth - 10;
          setShowScrollIndicator(canScroll && !isScrolledToEnd);
        }
      };
      
      checkScroll();
      const container = scrollContainerRef.current;
      if (container) {
        container.addEventListener('scroll', checkScroll);
        window.addEventListener('resize', checkScroll);
        return () => {
          container.removeEventListener('scroll', checkScroll);
          window.removeEventListener('resize', checkScroll);
        };
      }
    }, [isMobile, sessions]);
    
    return (
      <RecordsSection>
        <div style={{ 
          fontWeight: 900, 
          fontSize: '1.1rem', 
          marginBottom: '1rem',
          color: 'var(--accent-orange)',
          fontFamily: "'Poppins', sans-serif",
          position: 'relative',
          paddingLeft: '1rem',
          letterSpacing: '-0.02em'
        }}>
          <span style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: '6px',
            height: '100%',
            background: 'var(--accent-orange)'
          }}></span>
          {title}:
        </div>
        <div 
          ref={scrollContainerRef}
          style={{ 
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            width: '100%',
            position: 'relative',
            ...(isMobile ? { 
              marginLeft: '-1rem', 
              marginRight: '-1rem', 
              paddingLeft: '1rem', 
              paddingRight: '1rem',
              maxWidth: '100vw'
            } : {})
          }}
        >
        <RecordsTable style={{ fontSize: '0.75rem', width: '100%', tableLayout: isMobile ? 'auto' : 'fixed', minWidth: isMobile ? '600px' : 'auto' }}>
          <thead>
            <tr>
              <th style={{ width: isMobile ? '20px' : '30px', fontSize: '0.65rem', fontWeight: 'normal', padding: isMobile ? '0.25rem 0.1rem' : '0.25rem' }}></th>
              {filteredColumns.map(column => (
                <SortableHeader key={column.field} field={column.field} style={{ width: column.width }}>
                  {column.label}
                </SortableHeader>
              ))}
              <th style={{ width: '80px', fontSize: '0.65rem', fontWeight: 'bold', padding: '0.25rem', textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {groupedSessions.length === 0 ? (
              <tr>
                <td colSpan={totalColumns}>
                  <EmptyStateMessage>No {title.toLowerCase()} recorded yet.</EmptyStateMessage>
                </td>
              </tr>
            ) : (
              groupedSessions.map(group => {
                const isExpanded = expandedMonths.has(group.key);
                const toggleMonth = () => {
                  setExpandedMonths(prev => {
                    const newSet = new Set(prev);
                    if (newSet.has(group.key)) {
                      newSet.delete(group.key);
                    } else {
                      newSet.add(group.key);
                    }
                    return newSet;
                  });
                };
                
                return (
                  <React.Fragment key={group.key}>
                    <MonthDividerRow>
                      <MonthDividerCell colSpan={totalColumns}>
                        <MonthHeader 
                          onClick={toggleMonth}
                          style={{ cursor: 'pointer' }}
                        >
                          <span style={{ 
                            marginRight: isMobile ? '0.3rem' : '0.5rem', 
                            display: 'inline-flex',
                            alignItems: 'center',
                            width: isMobile ? '10px' : '12px',
                            height: isMobile ? '10px' : '12px'
                          }} aria-hidden>
                            {isExpanded ? (
                              <svg width="100%" height="100%" viewBox="0 0 12 12" fill="none" style={{ display: 'block' }}>
                                <path d="M2 4L6 8L10 4" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            ) : (
                              <svg width="100%" height="100%" viewBox="0 0 12 12" fill="none" style={{ display: 'block' }}>
                                <path d="M4 2L8 6L4 10" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                          </span>
                          <MonthLabel>{group.label}</MonthLabel>
                        </MonthHeader>
                      </MonthDividerCell>
                    </MonthDividerRow>
                    {isExpanded && group.sessions.map(session => (
                      <SessionRow key={session._id} session={session} columns={filteredColumns} />
                    ))}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </RecordsTable>
        </div>
        {/* Scroll indicator text */}
        {isMobile && showScrollIndicator && (
          <div style={{
            fontSize: '0.65rem',
            color: '#000',
            fontFamily: "'Poppins', sans-serif",
            marginTop: '1rem',
            marginBottom: '0.5rem',
            paddingLeft: '1rem',
            fontWeight: 400
          }}>
            Scroll sideways to see all data
          </div>
        )}
      </RecordsSection>
    );
  };

  const SortableHeader: React.FC<{ field: SortField; children: React.ReactNode; style?: React.CSSProperties }> = ({ field, children, style }) => (
    <TableHeader 
      onClick={() => handleSort(field)}
      style={{ 
        cursor: 'pointer', 
        userSelect: 'none',
        fontSize: '0.65rem',
        fontWeight: 'bold',
        padding: '0.25rem',
        textAlign: 'center',
        ...style
      }}
    >
      {children}
      {sortField === field && (
        <span style={{ marginLeft: '0.25rem' }}>
          {sortDirection === 'asc' ? '↑' : '↓'}
        </span>
      )}
    </TableHeader>
  );

  const notificationOverlay = notification ? (
    <div
      style={{
        position: 'fixed',
        top: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        background: notification.type === 'success' ? '#1f8b4c' : '#a30000',
        color: '#fff',
        padding: '0.85rem 1.5rem',
        borderRadius: '999px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
        fontFamily: "'Poppins', sans-serif",
        fontWeight: 600,
        letterSpacing: '0.01em'
      }}
    >
      {notification.message}
    </div>
  ) : null;

  if (isLoading) {
    return (
      <>
        <MessageModal
          isOpen={!!notification}
          type={notification?.type || 'success'}
          message={notification?.message || ''}
          onClose={() => setNotification(null)}
          autoCloseDelay={3000}
        />
        <MessageModal
          isOpen={!!error && error !== 'invalid_end_time' && error !== 'no_athletes_selected'}
          type="error"
          message={error || ''}
          onClose={() => setError(null)}
          autoCloseDelay={5000}
        />
        <TrainingSessionContainer>
          <PageHeader>
            <PageTitle>Training sessions</PageTitle>
          </PageHeader>
          <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-gray)' }}>
            Loading training sessions...
          </div>
        </TrainingSessionContainer>
      </>
    );
  }

  const renderEquipmentMessage = (field: 'suit' | 'skis' | 'shoes' | 'bindings') => {
    if (!formData.athleteId) return null;
    const selectedAthlete = athletes.find(a => a._id === formData.athleteId);
    const athletePlan = (selectedAthlete as any)?.subscription?.plan || 'free';
    const hasEquipmentPlan = hasPlanOrHigher(athletePlan, 'hs60');
    const countByType = {
      suit: getEquipmentByType('suit', formData.athleteId).length,
      skis: getEquipmentByType('skis', formData.athleteId).length,
      shoes: getEquipmentByType('shoes', formData.athleteId).length,
      bindings: getEquipmentByType('bindings', formData.athleteId).length,
    };
    const typeLabel = { suit: 'suits', skis: 'skis', shoes: 'shoes', bindings: 'bindings' };
    const hasNoneOfType = countByType[field] === 0;
    if (!hasEquipmentPlan) {
      return (
        <div
          ref={equipmentMessagePopoverRef}
          role="alert"
          style={{
            marginTop: '0.5rem',
            marginBottom: '0.5rem',
            padding: '0.6rem 0.85rem',
            maxWidth: '320px',
            background: '#fff',
            border: '1px solid var(--accent-orange)',
            borderRadius: '8px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            transition: 'opacity 0.25s ease-out',
            opacity: equipmentMessageDismissing ? 0 : 1,
          }}
        >
          <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-gray)' }}>
            Adding equipment is available on HS60 and higher plans.
          </p>
          <SubmitButton type="button" onClick={() => navigate('/pricing')} style={{ marginTop: '0.5rem', padding: '0.4rem 0.75rem', fontSize: '0.75rem' }}>
            View plans
          </SubmitButton>
        </div>
      );
    }
    if (hasNoneOfType) {
      const equipmentPath = user?.role === 'coach' ? '/coach/equipment' : '/jumper/equipment';
      return (
        <div
          ref={equipmentMessagePopoverRef}
          role="alert"
          style={{
            marginTop: '0.5rem',
            marginBottom: '0.5rem',
            padding: '0.6rem 0.85rem',
            maxWidth: '320px',
            background: '#fff',
            border: '1px solid var(--card-border)',
            borderRadius: '8px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            transition: 'opacity 0.25s ease-out',
            opacity: equipmentMessageDismissing ? 0 : 1,
          }}
        >
          <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-gray)' }}>
            You haven&apos;t added any {typeLabel[field]} yet.
          </p>
          <SubmitButton type="button" onClick={() => navigate(equipmentPath)} style={{ marginTop: '0.5rem', padding: '0.4rem 0.75rem', fontSize: '0.75rem' }}>
            Add equipment
          </SubmitButton>
        </div>
      );
    }
    return null;
  };

  return (
    <>
    <MessageModal
      isOpen={!!notification}
      type={notification?.type || 'success'}
      message={notification?.message || ''}
      onClose={() => setNotification(null)}
      autoCloseDelay={3000}
    />
    <MessageModal
      isOpen={!!error && error !== 'invalid_end_time' && error !== 'no_athletes_selected'}
      type="error"
      message={error || ''}
      onClose={() => setError(null)}
      autoCloseDelay={5000}
    />
    <TrainingSessionContainer>
      <PageHeader style={{ marginTop: '2.5rem' }}>
        <PageTitle>Training sessions</PageTitle>
        <PageSubtitle>
          Track all data from training sessions and get all the important information of your athletes.
        </PageSubtitle>
      </PageHeader>

      {/* Edit mode notification */}
      {(editingSessionId || editingJumpId || editingGroundTrainingId || editingCrossCountryId) && (
        <div style={{ 
          background: 'rgba(255, 193, 7, 0.2)', 
          border: '2px solid #ffc107', 
          borderRadius: '8px', 
          padding: '1rem', 
          marginBottom: '1rem',
          color: '#856404',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <span>⚠️</span>
          <span>You are currently editing data. Changes will be saved when you submit the form.</span>
        </div>
      )}

      {error && error !== 'invalid_end_time' && error !== 'no_athletes_selected' && (
        <div style={{ 
          background: 'rgba(255, 0, 0, 0.1)', 
          border: '1px solid #ff0000', 
          borderRadius: '8px', 
          padding: '1rem', 
          marginBottom: '1rem',
          color: '#ff0000'
        }}>
          {error}
        </div>
      )}
      
      {!showNewTrainingSessionForm ? (
        <div style={isMobile ? { display: 'flex', justifyContent: 'center', width: '100%', marginBottom: '0.5rem' } : {}}>
          <SubmitButton
            onClick={() => {
              setShowNewTrainingSessionForm(true);
              setTimeout(() => editFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
            }}
            style={{ marginTop: '0', marginBottom: '0.5rem', maxWidth: '320px', padding: '0.55rem 1.25rem', fontSize: '0.8rem' }}
          >
            START NEW TRAINING SESSION
          </SubmitButton>
        </div>
      ) : (
        <FormAreaWrapper ref={editFormRef}>
        <FormSection>
          <FormTitle>{editingSessionId ? 'Edit training session' : 'Start new training session'}</FormTitle>
        <form onSubmit={handleNewTrainingSessionSubmit}>
          <FormGrid>
            <FormGroup>
              <Label>Training Type</Label>
              <Select
                name="trainingType"
                value={editingSessionId && editingSessionData ? editingSessionData.trainingType : newTrainingSessionData.trainingType}
                onChange={(e) => {
                  if (editingSessionId && editingSessionData) {
                    setEditingSessionData({ ...editingSessionData, trainingType: e.target.value as 'ski_jumping' | 'ground_training' | 'cross_country' });
                  } else {
                    handleNewTrainingSessionChange(e);
                  }
                }}
                required
                disabled={isTrainingSessionActive}
                style={{ 
                  opacity: isTrainingSessionActive ? 0.6 : 1,
                  backgroundColor: isTrainingSessionActive ? '#f5f5f5' : '#fff'
                }}
              >
                <option value="ski_jumping">SKI JUMPING</option>
                <option value="ground_training">GROUND TRAINING</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Date</Label>
              <Input
                type="date"
                name="date"
                value={editingSessionId && editingSessionData ? editingSessionData.date : newTrainingSessionData.date}
                onChange={(e) => {
                  if (editingSessionId && editingSessionData) {
                    setEditingSessionData({ ...editingSessionData, date: e.target.value });
                  } else {
                    handleNewTrainingSessionChange(e);
                  }
                }}
                required
                disabled={isTrainingSessionActive}
                style={{ 
                  opacity: isTrainingSessionActive ? 0.6 : 1,
                  backgroundColor: isTrainingSessionActive ? '#f5f5f5' : '#fff'
                }}
              />
            </FormGroup>

            <FormGroup>
              <Label>Start Time</Label>
              <Input
                type="time"
                name="startTime"
                value={editingSessionId && editingSessionData ? editingSessionData.startTime : newTrainingSessionData.startTime}
                onChange={(e) => {
                  if (editingSessionId && editingSessionData) {
                    setEditingSessionData({ ...editingSessionData, startTime: e.target.value });
                  } else {
                    handleNewTrainingSessionChange(e);
                  }
                }}
                required
                disabled={isTrainingSessionActive}
                style={{ 
                  opacity: isTrainingSessionActive ? 0.6 : 1,
                  backgroundColor: isTrainingSessionActive ? '#f5f5f5' : '#fff'
                }}
              />
            </FormGroup>

            <FormGroup>
              <Label>
                Expected End Time
                {error === 'invalid_end_time' && (
                  <span style={{ 
                    color: '#ff0000', 
                    marginLeft: '0.5rem',
                    fontSize: '1.2em'
                  }}>
                    ⚠️
                  </span>
                )}
              </Label>
              <Input
                type="time"
                name="endTime"
                value={editingSessionId && editingSessionData ? editingSessionData.endTime : newTrainingSessionData.endTime}
                onChange={(e) => {
                  if (editingSessionId && editingSessionData) {
                    setEditingSessionData({ ...editingSessionData, endTime: e.target.value });
                  } else {
                    handleNewTrainingSessionChange(e);
                  }
                }}
                required
                disabled={isTrainingSessionActive}
                style={{ 
                  opacity: isTrainingSessionActive ? 0.6 : 1,
                  backgroundColor: isTrainingSessionActive ? '#f5f5f5' : '#fff',
                  border: error === 'invalid_end_time' ? '2px solid #ff0000' : undefined
                }}
              />
            </FormGroup>
          </FormGrid>

          <FormGrid>
            <FormGroup>
              <Label>Location</Label>
              <Input
                type="text"
                name="location"
                value={editingSessionId && editingSessionData ? editingSessionData.location : newTrainingSessionData.location}
                onChange={(e) => {
                  if (editingSessionId && editingSessionData) {
                    setEditingSessionData({ ...editingSessionData, location: e.target.value });
                  } else {
                    handleNewTrainingSessionChange(e);
                  }
                }}
                placeholder="Enter training location"
                required
                disabled={isTrainingSessionActive}
                style={{ 
                  opacity: isTrainingSessionActive ? 0.6 : 1,
                  backgroundColor: isTrainingSessionActive ? '#f5f5f5' : '#fff'
                }}
              />
            </FormGroup>

            <FormGroup>
              <Label>Weather</Label>
              <Select
                name="weather"
                value={editingSessionId && editingSessionData ? editingSessionData.weather : newTrainingSessionData.weather}
                onChange={(e) => {
                  if (editingSessionId && editingSessionData) {
                    setEditingSessionData({ ...editingSessionData, weather: e.target.value });
                  } else {
                    handleNewTrainingSessionChange(e);
                  }
                }}
                required
                disabled={isTrainingSessionActive}
                style={{ 
                  opacity: isTrainingSessionActive ? 0.6 : 1,
                  backgroundColor: isTrainingSessionActive ? '#f5f5f5' : '#fff'
                }}
              >
                <option value="">Select weather</option>
                {(newTrainingSessionData.trainingType === 'ground_training' || editingSessionData?.trainingType === 'ground_training') && (
                  <option value="indoor">INDOOR</option>
                )}
                <option value="hot">HOT</option>
                <option value="sunny">SUNNY</option>
                <option value="moderately_cloudy">MODERATELY CLOUDY</option>
                <option value="cloudy">CLOUDY</option>
                <option value="windy">WINDY</option>
                <option value="drizzle">DRIZZLE</option>
                <option value="light_rain">LIGHT RAIN</option>
                <option value="heavy_rain">HEAVY RAIN</option>
              </Select>
            </FormGroup>



          </FormGrid>

          {!filterAthleteId && (
            <FormGrid>
              <FormGroup style={{ gridColumn: 'span 2' }}>
                <Label>
                  Participating Athletes
                  {error === 'no_athletes_selected' && (
                    <span style={{ 
                      color: '#ff0000', 
                      marginLeft: '0.5rem',
                      fontSize: '1.2em'
                    }}>
                      ⚠️
                    </span>
                  )}
                </Label>
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  gap: '0.5rem',
                  maxHeight: '150px',
                  overflowY: 'auto',
                  borderRadius: '0.5rem',
                  padding: '0.5rem',
                  background: '#fff',
                  border: error === 'no_athletes_selected' ? '2px solid #ff0000' : undefined
                }}>
                  {athletes.map(athlete => {
                    const isSelected = (editingSessionId && editingSessionData ? editingSessionData.athleteIds : newTrainingSessionData.athleteIds).includes(athlete._id);
                    const isDisabled = isTrainingSessionActive || (filterAthleteId ? filterAthleteId !== athlete._id : false);
                    
                    return (
                      <label key={athlete._id} style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.5rem',
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        padding: '0.25rem',
                        borderRadius: '0.25rem',
                        background: isSelected ? 'var(--accent-orange)' : 'transparent',
                        color: isSelected ? '#fff' : 'var(--text-gray)',
                        opacity: isDisabled && !isSelected ? 0.5 : 1
                      }}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {
                            if (isDisabled) return;
                            if (editingSessionId && editingSessionData) {
                              const currentIds = editingSessionData.athleteIds;
                              setEditingSessionData({
                                ...editingSessionData,
                                athleteIds: currentIds.includes(athlete._id)
                                  ? currentIds.filter(id => id !== athlete._id)
                                  : [...currentIds, athlete._id],
                              });
                            } else {
                              handleAthleteSelection(athlete._id);
                            }
                          }}
                          disabled={isDisabled}
                          style={{ margin: 0, opacity: isDisabled ? 0.6 : 1 }}
                        />
                        {athlete.firstName} {athlete.lastName}
                      </label>
                    );
                  })}
                </div>
              </FormGroup>
            </FormGrid>
          )}

          <SubmitButton type="submit" disabled={isTrainingSessionActive}>
            {editingSessionId ? 'Update Training Session' : (isTrainingSessionActive ? 'Training Session Active' : 'Start Training Session')}
          </SubmitButton>
          {editingSessionId && (
            <SubmitButton 
              type="button" 
              onClick={handleCancelEdit}
              style={{ background: '#000', color: '#fff', border: 'none', marginTop: '1rem' }}
            >
              Cancel
            </SubmitButton>
          )}
        </form>
      </FormSection>
      </FormAreaWrapper>
      )}

      {showJumpForm && (
        <FormAreaWrapper ref={jumpFormRef}>
        <FormSection>
          <FormTitle>Insert new jump</FormTitle>
          {autoPopulatedFields.size > 0 && (
            <div style={{
              background: '#d4edda',
              border: '1px solid #c3e6cb',
              borderRadius: '0.5rem',
              padding: '0.75rem',
              marginBottom: '1rem',
              color: '#155724',
              fontSize: '0.9rem'
            }}>
              ✓ Fields auto-populated with last used values for the selected athlete
            </div>
          )}
        <form onSubmit={handleSubmit}>
          <JumpFormFieldsWrapper>
          {/* Row 1: Athlete and Hill Size */}
          <FormGrid className="jump-form-row-1" style={jumpFormRow1}>
            <FormGroup>
              <Label>Athlete</Label>
              <Select
                name="athleteId"
                value={formData.athleteId || filterAthleteId || ''}
                onChange={(e) => {
                  if (!filterAthleteId) {
                    handleAthleteSelectionInJumpForm(e.target.value);
                  }
                }}
                required
                disabled={!!filterAthleteId}
                style={{
                  opacity: filterAthleteId ? 0.7 : 1,
                  cursor: filterAthleteId ? 'not-allowed' : 'pointer',
                  fontSize: '0.9rem',
                  padding: '0.35rem 0.6rem'
                }}
              >
                <option value="">Select Athlete</option>
                {(() => {
                  // Get current session to filter athletes
                  const currentSessionId = (window as any).currentSessionId;
                  const currentSession = currentSessionId 
                    ? trainingSessions.find(s => s._id === currentSessionId)
                    : null;
                  
                  // Filter athletes: if session exists, show only participating athletes
                  const availableAthletes = currentSession && currentSession.athleteIds && currentSession.athleteIds.length > 0
                    ? athletes.filter(athlete => {
                        if (filterAthleteId) return athlete._id === filterAthleteId;
                        return currentSession.athleteIds.some((id: any) => {
                          const sessionAthleteId = typeof id === 'object' && id !== null && '_id' in id ? id._id : id;
                          return sessionAthleteId === athlete._id;
                        });
                      })
                    : athletes.filter(athlete => !filterAthleteId || athlete._id === filterAthleteId);
                  
                  return availableAthletes.map(athlete => (
                    <option key={athlete._id} value={athlete._id}>
                      {athlete.firstName} {athlete.lastName}
                    </option>
                  ));
                })()}
              </Select>
              {filterAthleteId && (
                <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.25rem', fontStyle: 'italic' }}>
                  Athlete is automatically selected
                </div>
              )}
            </FormGroup>

            <FormGroup>
              <Label>
                Hill Size
                {autoPopulatedFields.has('hillSize') && (
                  <span style={{ 
                    color: '#28a745', 
                    marginLeft: '0.5rem',
                    fontSize: '0.8em'
                  }}>
                    ✓ Auto-filled
                  </span>
                )}
              </Label>
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                name="hillSize"
                value={formData.hillSize}
                onChange={handleInputChange}
                placeholder="Enter hill size"
                style={{
                  border: autoPopulatedFields.has('hillSize') ? '2px solid #28a745' : undefined,
                  backgroundColor: autoPopulatedFields.has('hillSize') ? '#f8fff9' : undefined,
                  fontSize: '0.9rem',
                  padding: '0.35rem 0.6rem',
                  color: formData.hillSize === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                  WebkitTextFillColor: formData.hillSize === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                }}
              />
            </FormGroup>
          </FormGrid>

          {/* Row 2: Suit, Skis, Shoes, Bindings */}
          <FormGrid ref={equipmentRowRef} className="jump-form-row-2" style={jumpFormRow2}>
            <FormGroup>
              <Label>
                Suit
                {autoPopulatedFields.has('suitId') && (
                  <span style={{ 
                    color: '#28a745', 
                    marginLeft: '0.5rem',
                    fontSize: '0.8em'
                  }}>
                    ✓ Auto-filled
                  </span>
                )}
              </Label>
              <Select
                name="suitId"
                value={formData.suitId}
                onChange={handleInputChange}
                onFocus={() => setEquipmentMessageField('suit')}
                onBlur={() => setEquipmentMessageField(null)}
                style={{
                  border: autoPopulatedFields.has('suitId') ? '2px solid #28a745' : undefined,
                  backgroundColor: autoPopulatedFields.has('suitId') ? '#f8fff9' : undefined,
                  fontSize: '0.9rem',
                  padding: '0.35rem 0.6rem',
                  color: formData.suitId === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                  WebkitTextFillColor: formData.suitId === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                }}
              >
                <option value="">Select Suit (Optional)</option>
                {getEquipmentByType('suit', formData.athleteId).map(suit => (
                  <option key={suit._id} value={suit._id}>
                    {suit.name}
                  </option>
                ))}
              </Select>
            </FormGroup>
            {isMobile && equipmentMessageField === 'suit' && renderEquipmentMessage('suit')}

            <FormGroup>
              <Label>
                Skis
                {autoPopulatedFields.has('skisId') && (
                  <span style={{ 
                    color: '#28a745', 
                    marginLeft: '0.5rem',
                    fontSize: '0.8em'
                  }}>
                    ✓ Auto-filled
                  </span>
                )}
              </Label>
              <Select
                name="skisId"
                value={formData.skisId}
                onChange={handleInputChange}
                onFocus={() => setEquipmentMessageField('skis')}
                onBlur={() => setEquipmentMessageField(null)}
                style={{
                  border: autoPopulatedFields.has('skisId') ? '2px solid #28a745' : undefined,
                  backgroundColor: autoPopulatedFields.has('skisId') ? '#f8fff9' : undefined,
                  fontSize: '0.9rem',
                  padding: '0.35rem 0.6rem',
                  color: formData.skisId === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                  WebkitTextFillColor: formData.skisId === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                }}
              >
                <option value="">Select Skis (Optional)</option>
                {getEquipmentByType('skis', formData.athleteId).map(skis => (
                  <option key={skis._id} value={skis._id}>
                    {skis.name}
                  </option>
                ))}
              </Select>
            </FormGroup>
            {isMobile && equipmentMessageField === 'skis' && renderEquipmentMessage('skis')}

            <FormGroup>
              <Label>
                Shoes
                {autoPopulatedFields.has('shoesId') && (
                  <span style={{ 
                    color: '#28a745', 
                    marginLeft: '0.5rem',
                    fontSize: '0.8em'
                  }}>
                    ✓ Auto-filled
                  </span>
                )}
              </Label>
              <Select
                name="shoesId"
                value={formData.shoesId}
                onChange={handleInputChange}
                onFocus={() => setEquipmentMessageField('shoes')}
                onBlur={() => setEquipmentMessageField(null)}
                style={{
                  border: autoPopulatedFields.has('shoesId') ? '2px solid #28a745' : undefined,
                  backgroundColor: autoPopulatedFields.has('shoesId') ? '#f8fff9' : undefined,
                  fontSize: '0.9rem',
                  padding: '0.35rem 0.6rem',
                  color: formData.shoesId === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                  WebkitTextFillColor: formData.shoesId === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                }}
              >
                <option value="">Select Shoes (Optional)</option>
                {getEquipmentByType('shoes', formData.athleteId).map(shoes => (
                  <option key={shoes._id} value={shoes._id}>
                    {shoes.name}
                  </option>
                ))}
              </Select>
            </FormGroup>
            {isMobile && equipmentMessageField === 'shoes' && renderEquipmentMessage('shoes')}

            <FormGroup>
              <Label>
                Bindings
                {autoPopulatedFields.has('bindingsId') && (
                  <span style={{ 
                    color: '#28a745', 
                    marginLeft: '0.5rem',
                    fontSize: '0.8em'
                  }}>
                    ✓ Auto-filled
                  </span>
                )}
              </Label>
              <Select
                name="bindingsId"
                value={formData.bindingsId}
                onChange={handleInputChange}
                onFocus={() => setEquipmentMessageField('bindings')}
                onBlur={() => setEquipmentMessageField(null)}
                style={{
                  border: autoPopulatedFields.has('bindingsId') ? '2px solid #28a745' : undefined,
                  backgroundColor: autoPopulatedFields.has('bindingsId') ? '#f8fff9' : undefined,
                  fontSize: '0.9rem',
                  padding: '0.35rem 0.6rem',
                  color: formData.bindingsId === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                  WebkitTextFillColor: formData.bindingsId === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                }}
              >
                <option value="">Select Bindings (Optional)</option>
                {getEquipmentByType('bindings', formData.athleteId).map(bindings => (
                  <option key={bindings._id} value={bindings._id}>
                    {bindings.name}
                  </option>
                ))}
              </Select>
            </FormGroup>
            {isMobile && equipmentMessageField === 'bindings' && renderEquipmentMessage('bindings')}
          </FormGrid>

          {/* Equipment message – desktop: below equipment row; mobile: shown after each selector above */}
          {!isMobile && formData.athleteId && equipmentMessageField && renderEquipmentMessage(equipmentMessageField)}

          {/* Row 3: Starting Gate, Distance, Wind, Speed */}
            <FormGrid className="jump-form-row-3" style={jumpFormRow3}>
              <FormGroup>
                <Label>
                  Starting Gate
                  {autoPopulatedFields.has('startingGate') && (
                    <span style={{ 
                      color: '#28a745', 
                      marginLeft: '0.5rem',
                      fontSize: '0.8em'
                    }}>
                      ✓ Auto-filled
                    </span>
                  )}
                </Label>
                <Input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={100}
                  name="startingGate"
                  value={formData.startingGate === 0 ? '0' : String(formData.startingGate)}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || /^\d+$/.test(value)) {
                      const numValue = value === '' ? 0 : parseInt(value, 10);
                      if (value === '' || (numValue >= 0 && numValue <= 100)) {
                        setFormData(prev => ({
                          ...prev,
                          startingGate: numValue
                        }));
                      }
                    }
                  }}
                  style={{
                    border: autoPopulatedFields.has('startingGate') ? '2px solid #28a745' : undefined,
                    backgroundColor: autoPopulatedFields.has('startingGate') ? '#f8fff9' : undefined,
                    fontSize: '0.9rem',
                    padding: '0.35rem 0.6rem',
                    width: '100%',
                    color: formData.startingGate === 0 ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                    WebkitTextFillColor: formData.startingGate === 0 ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                  }}
                />
              </FormGroup>

              <FormGroup>
                <Label>Distance (meters)</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  name="distance"
                  value={rawInputValues.distance === '' && formData.distance === 0 ? '0' : rawInputValues.distance}
                  onChange={(e) => {
                    let value = e.target.value;
                    // Remove any non-numeric characters except comma and period
                    value = value.replace(/[^\d,.-]/g, '');
                    // Allow both comma and period as decimal separator, but only one
                    const hasComma = value.includes(',');
                    const hasPeriod = value.includes('.');
                    if (hasComma && hasPeriod) {
                      // If both exist, keep the last one
                      const lastComma = value.lastIndexOf(',');
                      const lastPeriod = value.lastIndexOf('.');
                      if (lastComma > lastPeriod) {
                        value = value.replace(/\./g, '');
                      } else {
                        value = value.replace(/,/g, '');
                      }
                    }
                    if (value === '' || /^-?\d*[,.]?\d*$/.test(value)) {
                      const normalizedValue = value.replace(',', '.');
                      setRawInputValues(prev => ({ ...prev, distance: value }));
                      const numValue = normalizedValue === '' || normalizedValue === '-' ? 0 : parseFloat(normalizedValue) || 0;
                      if (normalizedValue === '' || normalizedValue === '-' || (numValue >= 0 && numValue <= 999)) {
                        setFormData(prev => ({ ...prev, distance: numValue }));
                      }
                    }
                  }}
                  onBlur={(e) => {
                    const value = e.target.value;
                    if (value === '' || value === '-') {
                      setRawInputValues(prev => ({ ...prev, distance: '' }));
                    } else {
                      const normalizedValue = value.replace(',', '.');
                      const numValue = parseFloat(normalizedValue) || 0;
                      setRawInputValues(prev => ({ ...prev, distance: String(numValue).replace('.', ',') }));
                    }
                  }}
                  style={{
                    fontSize: '0.9rem',
                    padding: '0.35rem 0.6rem',
                    width: '100%',
                    color: (rawInputValues.distance === '' && formData.distance === 0) ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                    WebkitTextFillColor: (rawInputValues.distance === '' && formData.distance === 0) ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                  }}
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  Wind (km/h)
                  {autoPopulatedFields.has('wind') && (
                    <span style={{ 
                      color: '#28a745', 
                      marginLeft: '0.5rem',
                      fontSize: '0.8em'
                    }}>
                      ✓ Auto-filled
                    </span>
                  )}
                </Label>
                <Input
                  type="text"
                  inputMode="text"
                  name="wind"
                  value={formData.wind === '' ? '0' : formData.wind}
                  onChange={handleInputChange}
                  style={{
                    border: autoPopulatedFields.has('wind') ? '2px solid #28a745' : undefined,
                    backgroundColor: autoPopulatedFields.has('wind') ? '#f8fff9' : undefined,
                    fontSize: '0.9rem',
                    padding: '0.35rem 0.6rem',
                    width: '100%',
                    color: formData.wind === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                    WebkitTextFillColor: formData.wind === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                  }}
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  Speed (km/h)
                  {autoPopulatedFields.has('speed') && (
                    <span style={{ 
                      color: '#28a745', 
                      marginLeft: '0.5rem',
                      fontSize: '0.8em'
                    }}>
                      ✓ Auto-filled
                    </span>
                  )}
                </Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  name="speed"
                  value={rawInputValues.speed === '' && formData.speed === 0 ? '0' : rawInputValues.speed}
                  onChange={(e) => {
                    let value = e.target.value;
                    value = value.replace(/[^\d,.-]/g, '');
                    const hasComma = value.includes(',');
                    const hasPeriod = value.includes('.');
                    if (hasComma && hasPeriod) {
                      const lastComma = value.lastIndexOf(',');
                      const lastPeriod = value.lastIndexOf('.');
                      if (lastComma > lastPeriod) {
                        value = value.replace(/\./g, '');
                      } else {
                        value = value.replace(/,/g, '');
                      }
                    }
                    if (value === '' || /^-?\d*[,.]?\d*$/.test(value)) {
                      const normalizedValue = value.replace(',', '.');
                      setRawInputValues(prev => ({ ...prev, speed: value }));
                      const numValue = normalizedValue === '' || normalizedValue === '-' ? 0 : parseFloat(normalizedValue) || 0;
                      if (normalizedValue === '' || normalizedValue === '-' || (numValue >= 0 && numValue <= 999)) {
                        setFormData(prev => ({ ...prev, speed: numValue }));
                      }
                    }
                  }}
                  onBlur={(e) => {
                    const value = e.target.value;
                    if (value === '' || value === '-') {
                      setRawInputValues(prev => ({ ...prev, speed: '' }));
                    } else {
                      const normalizedValue = value.replace(',', '.');
                      const numValue = parseFloat(normalizedValue) || 0;
                      setRawInputValues(prev => ({ ...prev, speed: String(numValue).replace('.', ',') }));
                    }
                  }}
                  style={{
                    border: autoPopulatedFields.has('speed') ? '2px solid #28a745' : undefined,
                    backgroundColor: autoPopulatedFields.has('speed') ? '#f8fff9' : undefined,
                    fontSize: '0.9rem',
                    padding: '0.35rem 0.6rem',
                    width: '100%',
                    color: (rawInputValues.speed === '' && formData.speed === 0) ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                    WebkitTextFillColor: (rawInputValues.speed === '' && formData.speed === 0) ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                  }}
                />
              </FormGroup>
            </FormGrid>

            {/* Row 4: Rating and Telemark Landing (on mobile: Rating order 3, Equipment order 4, Telemark order 5) */}
            <FormGrid className="jump-form-row-4" style={jumpFormRow4}>
              <FormGroup className="jump-form-row-4-rating">
                <Label>Rating (1-10)</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  name="rating"
                  value={rawInputValues.rating}
                  onChange={(e) => {
                    let value = e.target.value;
                    // Remove any non-numeric characters except comma and period
                    value = value.replace(/[^\d,.-]/g, '');
                    // Allow both comma and period as decimal separator, but only one
                    const hasComma = value.includes(',');
                    const hasPeriod = value.includes('.');
                    if (hasComma && hasPeriod) {
                      // If both exist, keep the last one
                      const lastComma = value.lastIndexOf(',');
                      const lastPeriod = value.lastIndexOf('.');
                      if (lastComma > lastPeriod) {
                        value = value.replace(/\./g, '');
                      } else {
                        value = value.replace(/,/g, '');
                      }
                    }
                    // Only allow numbers and one decimal separator
                    if (value === '' || /^-?\d*[,.]?\d*$/.test(value)) {
                      setRawInputValues(prev => ({ ...prev, rating: value }));
                      const normalizedValue = value.replace(',', '.');
                      const numValue = normalizedValue === '' || normalizedValue === '-' ? 0 : parseFloat(normalizedValue) || 0;
                      if (normalizedValue === '' || normalizedValue === '-' || (numValue >= 0 && numValue <= 10)) {
                        setFormData(prev => ({
                          ...prev,
                          rating: numValue
                        }));
                      }
                    }
                  }}
                  onBlur={(e) => {
                    // On blur, ensure the value is properly formatted
                    const value = e.target.value;
                    if (value === '' || value === '-') {
                      setRawInputValues(prev => ({ ...prev, rating: '' }));
                    } else {
                      const normalizedValue = value.replace(',', '.');
                      const numValue = parseFloat(normalizedValue) || 0;
                      setRawInputValues(prev => ({ ...prev, rating: String(numValue).replace('.', ',') }));
                    }
                  }}
                  placeholder="1–10"
                  style={{
                    fontSize: '0.9rem',
                    padding: '0.35rem 0.6rem',
                    color: rawInputValues.rating === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                    WebkitTextFillColor: rawInputValues.rating === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                  }}
                />
              </FormGroup>

              <FormGroup className="telemark-section jump-form-row-4-telemark">
                <Label>Telemark Landing</Label>
                <TelemarkToggle>
                  <ToggleButton
                    type="button"
                    active={formData.telemark}
                    onClick={() => handleTelemarkToggle(true)}
                  >
                    Yes
                  </ToggleButton>
                  <ToggleButton
                    type="button"
                    active={!formData.telemark}
                    onClick={() => handleTelemarkToggle(false)}
                  >
                    No
                  </ToggleButton>
                </TelemarkToggle>
              </FormGroup>
            </FormGrid>
          </JumpFormFieldsWrapper>

          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', alignItems: 'center', flexWrap: 'wrap', width: '100%' }}>
            <SubmitButton 
              type="submit" 
              style={{ 
                marginBottom: '0', 
                flex: '1 1 0',
                minWidth: '120px',
                maxWidth: '100%'
              }}
            >
              {editingJumpId ? 'Update Jump' : 'Import Jump'}
            </SubmitButton>
            
            {/* Camera icon button for video recording - HIDDEN FOR NOW */}
            {/* {(window as any).currentSessionId && (
              <button
                type="button"
                onClick={() => {
                  setShowVideoRecorder(!showVideoRecorder);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0.75rem',
                  background: showVideoRecorder ? 'var(--accent-orange-dark)' : 'var(--accent-orange)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  minWidth: '48px',
                  height: '48px',
                  marginBottom: '0',
                  flexShrink: 0
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--accent-orange-dark)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = showVideoRecorder ? 'var(--accent-orange-dark)' : 'var(--accent-orange)';
                }}
                title="Record Jump Video"
              >
                <Icon component={FiCamera} size={20} />
              </button>
            )} */}
            
            {editingJumpId && (
              <SubmitButton 
                type="button" 
                onClick={() => {
                  setEditingJumpId(null);
                  setShowJumpForm(false);
                  setFormData(prev => ({
                    ...prev,
                    distance: 0,
                    startingGate: 0,
                    telemark: false,
                    hillSize: '',
                    wind: '',
                    speed: 0,
                    rating: 0,
                  }));
                  setRawInputValues({ distance: '', speed: '', rating: '' });
                }}
                style={{ 
                  background: '#000',
                  color: '#fff',
                  border: 'none',
                  flex: '1 1 0',
                  minWidth: '120px',
                  maxWidth: '100%'
                }}
              >
                Cancel
              </SubmitButton>
            )}
          </div>
          
          {/* Video Recorder Component - HIDDEN FOR NOW */}
          {/* {showVideoRecorder && (window as any).currentSessionId && (
            <div style={{ marginTop: '1rem', padding: '1rem', border: '2px solid var(--accent-orange)', borderRadius: '8px', background: 'var(--bg-sidebar)' }}>
              <VideoRecorder
                sessionId={(window as any).currentSessionId}
                jumpId={editingJumpId || 'new'}
                onVideoRecorded={(videoUrl) => {
                  console.log('Video recorded and uploaded:', videoUrl);
                  // Refresh the session data to show the video link
                  const fetchSessions = async () => {
                    try {
                      const sessionsResponse = await trainingSessionAPI.getAll(undefined, undefined, true);
                      const sessionsArray = Array.isArray(sessionsResponse) ? sessionsResponse : (sessionsResponse?.sessions || []);
                      setTrainingSessions(sessionsArray);
                    } catch (error) {
                      console.error('Error fetching sessions:', error);
                    }
                  };
                  fetchSessions();
                }}
              />
            </div>
          )} */}
        </form>
        
        {isTrainingSessionActive && newTrainingSessionData.trainingType === 'ski_jumping' && (
          <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
            <FormGroup>
              <Label>Coach's Description / Notes</Label>
              <textarea
                value={coachDescription}
                onChange={(e) => setCoachDescription(e.target.value)}
                placeholder="Write your thoughts about this training session..."
                style={{
                  width: '100%',
                  minHeight: '100px',
                  padding: '0.7rem',
                  fontSize: '1rem',
                  borderRadius: '0.5rem',
                  border: '1px solid #e0e6ed',
                  fontFamily: 'Poppins, sans-serif',
                  resize: 'vertical'
                }}
              />
            </FormGroup>
            <div style={{ 
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: '0.5rem',
              justifyContent: 'flex-end',
              marginTop: '1rem',
              width: '100%'
            }}>
              <SubmitButton
                type="button"
                onClick={handleFinishTrainingSession}
                style={{
                  background: '#dc3545',
                  color: '#fff',
                  border: '2px solid #dc3545',
                  boxShadow: 'none',
                  padding: isMobile ? '0.5rem 1rem' : '0.7rem 1.5rem',
                  fontSize: isMobile ? '0.7rem' : '0.85rem'
                }}
              >
                FINISH TRAINING SESSION
              </SubmitButton>
              {(window as any).currentSessionId && (
                <>
                  {trainingSessions.find(s => s._id === (window as any).currentSessionId)?.isPaused ? (
                    <SubmitButton
                      type="button"
                      onClick={() => {
                        const sessionId = (window as any).currentSessionId;
                        if (sessionId) handleContinueSession(sessionId);
                      }}
                      style={{
                        background: '#28a745',
                        boxShadow: '0 2px 8px rgba(40, 167, 69, 0.2)',
                        padding: isMobile ? '0.5rem 1rem' : '0.7rem 1.5rem',
                        fontSize: isMobile ? '0.7rem' : '0.85rem'
                      }}
                    >
                      CONTINUE SESSION
                    </SubmitButton>
                  ) : (
                    <SubmitButton
                      type="button"
                      onClick={() => {
                        const sessionId = (window as any).currentSessionId;
                        if (sessionId) handlePauseSession(sessionId);
                      }}
                      style={{
                        background: '#000',
                        color: '#fff',
                        border: '2px solid #000',
                        boxShadow: 'none',
                        padding: isMobile ? '0.5rem 1rem' : '0.7rem 1.5rem',
                        fontSize: isMobile ? '0.7rem' : '0.85rem'
                      }}
                    >
                      PAUSE SESSION
                    </SubmitButton>
                  )}
                </>
              )}
            </div>
          </div>
        )}
        </FormSection>
        </FormAreaWrapper>
      )}

      {showGroundTrainingForm && (
        <FormAreaWrapper>
        <FormSection>
          <FormTitle>Insert Ground Training Data</FormTitle>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              
              // If editing, use update handler
              if (editingGroundTrainingId) {
                return handleUpdateGroundTraining(e);
              }
              
              const currentSessionId = (window as any).currentSessionId;
              if (!currentSessionId) return;
              
              try {
                // Save all ground training records to backend
                await Promise.all(groundTrainings.map(async (record) => {
                  await trainingSessionAPI.addGroundTraining(currentSessionId, record);
                }));
                
                // Refresh training sessions - reload all
                let updatedSessionsResponse;
                if (filterAthleteId) {
                  updatedSessionsResponse = await trainingSessionAPI.getByAthlete(filterAthleteId, undefined, undefined, true);
                } else {
                  updatedSessionsResponse = await trainingSessionAPI.getAll(undefined, undefined, true);
                }
                const sessionsArray = Array.isArray(updatedSessionsResponse) ? updatedSessionsResponse : (updatedSessionsResponse?.sessions || []);
                setTrainingSessions(sessionsArray);
                
                // Reset form and state
                setIsTrainingSessionActive(false);
                setShowGroundTrainingForm(false);
                setGroundTrainings([]);
                setNewTrainingSessionData({
                  trainingType: 'ski_jumping',
                  date: new Date().toISOString().split('T')[0],
                  startTime: '',
                  endTime: '',
                  location: '',
                  weather: '',
                  athleteIds: [],
                });
                (window as any).currentSessionId = null;
                
                setError(null);
              } catch (err) {
                console.error('Error saving ground training data:', err);
                setError('Failed to save ground training data. Please try again.');
              }
            }}
          >
            {/* Headers */}
            <FormGrid style={{ marginBottom: '1rem', borderBottom: '2px solid #e0e6ed', paddingBottom: '0.5rem' }}>
              <FormGroup>
                <Label style={{ marginBottom: '0.5rem', fontSize: '0.9rem', color: '#666' }}>ATHLETE</Label>
              </FormGroup>
              <FormGroup>
                <Label style={{ marginBottom: '0.5rem', fontSize: '0.9rem', color: '#666' }}>TRAINING TYPE</Label>
              </FormGroup>
              <FormGroup>
                <Label style={{ marginBottom: '0.5rem', fontSize: '0.9rem', color: '#666' }}>INTENSITY (1-10)</Label>
              </FormGroup>
              <FormGroup>
                <Label style={{ marginBottom: '0.5rem', fontSize: '0.9rem', color: '#666' }}>ACTIONS</Label>
              </FormGroup>
            </FormGrid>

            {/* Athlete rows */}
            {newTrainingSessionData.athleteIds.map((athleteId, idx) => {
              const athlete = athletes.find(a => a._id === athleteId);
              if (!athlete) return null;
              const record = groundTrainings[idx] || {
                _id: '',
                athleteId,
                trainingType: 'coordination',
                intensity: 1,
                createdAt: '',
              };
              return (
                <FormGrid key={athleteId} style={{ marginBottom: '1rem', alignItems: 'center' }}>
                  <FormGroup>
                    <div style={{ 
                      fontSize: '1rem', 
                      color: '#222', 
                      fontFamily: 'Poppins, sans-serif', 
                      fontWeight: 600, 
                      padding: '0.4rem 0.7rem',
                      backgroundColor: '#f5f8fa',
                      borderRadius: '0.5rem',
                      border: '1px solid #e0e6ed'
                    }}>
                      {athlete.firstName} {athlete.lastName}
                    </div>
                  </FormGroup>
                  <FormGroup>
                    <Select
                      value={record.trainingType}
                      onChange={e => {
                        const updated = [...groundTrainings];
                        updated[idx] = { ...record, trainingType: e.target.value as any };
                        setGroundTrainings(updated);
                      }}
                      required
                      style={{ width: '100%' }}
                    >
                      <option value="coordination">Coordination</option>
                      <option value="power">Power</option>
                      <option value="speed">Speed</option>
                      <option value="stretching">Stretching</option>
                      <option value="specialized_coordination">Specialized coordination</option>
                      <option value="imitation_ski_jump">Imitation of Ski Jump</option>
                      <option value="run">Run</option>
                      <option value="visualisation">Visualisation</option>
                    </Select>
                  </FormGroup>
                  <FormGroup>
                    <Input
                      type="number"
                      min={1}
                      max={10}
                      value={record.intensity}
                      onChange={e => {
                        const updated = [...groundTrainings];
                        updated[idx] = { ...record, intensity: Number(e.target.value) };
                        setGroundTrainings(updated);
                      }}
                      required
                      placeholder="1-10"
                      style={{ width: '100%' }}
                    />
                  </FormGroup>
                  <FormGroup>
                    <div style={{ fontSize: '0.8rem', color: '#666', fontStyle: 'italic' }}>
                      Row {idx + 1}
                    </div>
                  </FormGroup>
                </FormGrid>
              );
            })}
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
              <SubmitButton type="submit">
                {editingGroundTrainingId ? 'Update Ground Training' : 'Save Ground Training Data'}
              </SubmitButton>
              {editingGroundTrainingId && (
                <SubmitButton 
                  type="button" 
                  onClick={() => {
                    setEditingGroundTrainingId(null);
                    setShowGroundTrainingForm(false);
                    setFormData(prev => ({
                      ...prev,
                      groundTrainingType: 'coordination',
                      intensity: 0,
                    }));
                    (window as any).currentSessionId = null;
                  }}
                  style={{ background: '#000', color: '#fff', border: 'none' }}
                >
                  Cancel
                </SubmitButton>
              )}
            </div>
          </form>
        </FormSection>
        </FormAreaWrapper>
      )}

             <TrainingSessionsTable
        title="Ski Jumping Sessions"
        sessions={getSkiJumpingSessions()}
        columns={[
          { field: 'date', label: 'Date' },
          { field: 'location', label: 'Location' },
          { field: 'weather', label: 'Weather' },
          { field: 'distance', label: 'Jumps' },
          { field: 'avgDistance', label: 'Avg Distance' }
        ]}
        expandedMonths={expandedMonthsSkiJumping}
        setExpandedMonths={setExpandedMonthsSkiJumping}
      />

             <div style={{ marginBottom: '1rem' }}>
               <TrainingSessionsTable
                 title="Ground Training Sessions"
                 sessions={getGroundTrainingSessions()}
                 columns={[
                   { field: 'date', label: 'Date' },
                   { field: 'location', label: 'Location' },
                   { field: 'weather', label: 'Weather' },
                   { field: 'distance', label: 'Records' },
                   { field: 'avgIntensity', label: 'Avg Intensity' }
                 ]}
                 expandedMonths={expandedMonthsGroundTraining}
                 setExpandedMonths={setExpandedMonthsGroundTraining}
               />
               
               {sessionInsights.avgGroundIntensity !== null && (
                 <div style={{
                   marginTop: '1rem',
                   padding: '0.75rem 1rem',
                   background: 'rgba(255, 150, 0, 0.1)',
                   borderRadius: '0.5rem',
                   border: '1px solid rgba(255, 150, 0, 0.2)',
                   display: 'inline-block'
                 }}>
                   <span style={{
                     fontSize: '0.9rem',
                     fontWeight: 600,
                     color: 'var(--text-gray)',
                     fontFamily: "'Poppins', sans-serif"
                   }}>
                     Overall Avg. Ground Intensity: {' '}
                     <span style={{
                       color: 'var(--accent-orange)',
                       fontWeight: 800,
                       fontSize: '1rem'
                     }}>
                       {sessionInsights.avgGroundIntensity.toFixed(1)}/10
                     </span>
                   </span>
                 </div>
               )}
             </div>

      {/* Confirmation Modal for Finishing Training Session */}
      {showFinishConfirmation && (
        <ConfirmationModalOverlay onClick={() => setShowFinishConfirmation(false)}>
          <ConfirmationModalContent onClick={e => e.stopPropagation()}>
            <h2>Finish Training Session</h2>
            <ConfirmationModalBody>
              <p>Are you sure you want to finish this training session?</p>
              <p>This will save all recorded data and close the session. You can view the session in your training sessions list.</p>
            </ConfirmationModalBody>
            <ConfirmationButtonGroup>
              <ConfirmationModalButton
                type="button"
                $variant="secondary"
                onClick={() => setShowFinishConfirmation(false)}
              >
                Cancel
              </ConfirmationModalButton>
              <ConfirmationModalButton
                type="button"
                $variant="primary"
                onClick={confirmFinishTrainingSession}
              >
                Finish Session
              </ConfirmationModalButton>
            </ConfirmationButtonGroup>
          </ConfirmationModalContent>
        </ConfirmationModalOverlay>
      )}
      
    </TrainingSessionContainer>
    </>
  );
};

export default TrainingSessions; 